(function () {
  'use strict';

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  // Matches the Liquid `money_without_trailing_zeros` filter already used for every other "+$X" swatch/pill
  // price tag (~line 470/502 of fyre-template-product.liquid) — only the full price (FyrePrice/FyreATC) keeps
  // cents, matching `current_variant.price | money`.
  function formatMoneyTrim(cents) {
    return formatMoney(cents).replace(/\.00$/, '');
  }

  // ── Pad-swap cart contract ────────────────────────────────────────────────────────────────────────
  // These three strings ARE the interface to NetSuite. The swap is priced by hand in NetSuite, not by any
  // Shopify discount: order management adds the chosen pad's cost and credits back the pad that shipped in the
  // kit. Shopify's whole obligation is to put enough on the order for someone to do that. A property only
  // arrives if the connector's Mappings > Order item table carries a row for it, and an unmapped property is
  // dropped silently — so renaming a key here means changing that row in the same breath.
  // Leading underscore keeps them out of the rendered cart: they are plumbing, not customer-facing copy.
  // Jacob's B2B quick-order form (assets/algolia-b2b-order-form.js) is the base vocabulary — Paul, 2026-08-25:
  // "Let's just adopt Jacob's props for 244?" — with two amendments, both of which his schema needs anyway:
  //   1. _default_pad_sku is NEW. His kit line names only the pad swapped TO, so his Function has no credit
  //      baseline to work from and cannot compute the discount from cart data alone.
  //   2. none. The pairing token is _pair_id, the same key the Custom Color paint add-on uses. Sharing it is
  //      safe because every consumer disambiguates by PRODUCT HANDLE, not by the token: the cart snippet
  //      hides handle 'pad-swap' and links handle 'custom-caliper-paint' on separate branches. Qualifying the
  //      key would instead make the $0 marker line render to the buyer, since that gate reads _pair_id.
  var PADSWAP_PAIR_KEY = '_pair_id';                // joins the kit line to its pad + marker lines
  var PADSWAP_ADDON_KEY = '_pair_addon';            // marks the chosen-pad line as a linked row in the cart
  var PADSWAP_CHOSEN_KEY = '_non_default_pad_variant_id'; // Jacob's name, on the kit line: the pad swapped TO
  var PADSWAP_DEFAULT_KEY = '_default_pad_sku';     // amendment 1 — the pad ALREADY PRICED INTO the kit

  // The $0 untracked marker product (SKU PADSWAP) the swap hangs off. It carries no money — the credit amount
  // depends on the buyer's B2B catalog price, which the theme cannot resolve — so the marker is a SIGNAL: it
  // tells the discount Function a swap happened on this cart and names both SKUs.
  // Read at submit time, not at script evaluation: this asset can be evaluated before the section markup is
  // parsed, and an id resolved to null once would stay null for the life of the page.
  // Custom paint and the colour swatches are exclusive on screen. Custom leaves the colour radio CHECKED
  // underneath (variant selection needs a colour axis), so re-clicking that same swatch fires no `change` —
  // this runs on `click`, which every pick fires. Returns true when it turned Custom off.
  function releaseCustomForColorPick(toggleEl, pill, colorIdx) {
    if (!toggleEl || !toggleEl.checked || parseInt(pill.dataset.optionIndex, 10) !== colorIdx) return false;
    toggleEl.checked = false;
    toggleEl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // Custom paint is sold as a separate add-on line. With the toggle on, a submit that finds no Custom Caliper
  // Paint variant for this piston count + stage must NOT reach the cart: the kit line has already been swapped to
  // a colour sibling and would ship unpainted at the base price. Returns true when the submit must be refused.
  function customPaintUnmatched(customPaintActive, caliperMatch) {
    return !!customPaintActive && !caliperMatch;
  }

  // The `change` twin of the click release, for keyboard radio navigation. fyre-resolve.js dispatches SYNTHETIC
  // change events on these same radios when it auto-corrects an unavailable colour — those must never release
  // Custom, or a vehicle change silently drops the paint line. Only a user-initiated event releases.
  function releaseCustomForColorChange(evt, toggleEl, pill, colorIdx) {
    if (!evt || !evt.isTrusted) return false;
    return releaseCustomForColorPick(toggleEl, pill, colorIdx);
  }

  function padSwapVariantId() {
    var el = document.getElementById('pdp-padswap-variant');
    var n = el ? parseInt(el.textContent, 10) : NaN;
    return isNaN(n) ? null : n;
  }
  /* ── Gallery ── */
  var FyreGallery = {
    el: null, mainImg: null, mainSource: null, thumbs: null,

    init: function () {
      this.el = document.querySelector('[data-pdp-gallery]');
      if (!this.el) return;
      this.mainImg = this.el.querySelector('[data-pdp-main-image]');
      this.mainSource = this.mainImg
        ? this.mainImg.closest('picture') && this.mainImg.closest('picture').querySelector('source[type="image/webp"]')
        : null;
      this.thumbs = this.el.querySelectorAll('[data-pdp-thumb]');
      this.bindThumbnails();
      this.initVideoPreview();
      this.initThumbsScroll();
    },

    initThumbsScroll: function () {
      var thumbsCol = this.el.querySelector('.pdp-gallery__thumbs--vertical');
      if (!thumbsCol) return;
      function check() {
        var isVertical = window.innerWidth > 767;
        var atEnd = isVertical
          ? thumbsCol.scrollTop + thumbsCol.clientHeight >= thumbsCol.scrollHeight - 2
          : thumbsCol.scrollLeft + thumbsCol.clientWidth >= thumbsCol.scrollWidth - 2;
        thumbsCol.classList.toggle('pdp-gallery__thumbs--at-end', atEnd);
      }
      thumbsCol.addEventListener('scroll', check, { passive: true });
      check();
    },

    initVideoPreview: function () {
      var preview = document.querySelector('[data-video-preview]');
      if (!preview) return;
      var videoId = preview.dataset.videoPreview;
      if (!videoId) return;
      function loadPreviewIframe() {
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + videoId +
          '?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&playlist=' + videoId;
        iframe.allow = 'autoplay; encrypted-media';
        iframe.setAttribute('allowfullscreen', '');
        iframe.title = 'Video preview';
        iframe.className = 'pdp-gallery__video-iframe';
        iframe.addEventListener('load', function () { iframe.classList.add('is-loaded'); }, { once: true });
        var img = preview.querySelector('img');
        if (img) img.replaceWith(iframe);
      }
      if (document.readyState === 'complete') loadPreviewIframe();
      else window.addEventListener('load', loadPreviewIframe, { once: true });
    },

    bindThumbnails: function () {
      var self = this;
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (thumb.classList.contains('pdp-gallery__thumb--active')) return;
          self.setActiveThumb(thumb);
          self.swapImageBySrc(thumb.dataset.pdpSrc, thumb.dataset.pdpSrcset);
        });
      });
    },

    setActiveThumb: function (activeThumb) {
      this.thumbs.forEach(function (t) {
        t.classList.toggle('pdp-gallery__thumb--active', t === activeThumb);
      });
    },

    swapImageBySrc: function (src, srcset) {
      var img = this.mainImg;
      var source = this.mainSource;
      if (!img || !src) return;
      img.style.opacity = '0';
      function onLoaded() { img.style.opacity = '1'; img.removeEventListener('load', onLoaded); }
      img.addEventListener('load', onLoaded);
      if (srcset) {
        img.srcset = srcset;
        if (source) {
          source.srcset = srcset.replace(/([^\s,]+)((?:\s+\d+w)?)/g, function (_, url, size) {
            return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'format=webp' + size;
          });
        }
      }
      img.src = src;
      if (img.complete) onLoaded();
    },

    swapToVariantImage: function (src) {
      if (!src) return;
      var matched = null;
      this.thumbs.forEach(function (t) {
        if (t.dataset.pdpSrc && t.dataset.pdpSrc.split('?')[0] === src.split('?')[0]) matched = t;
      });
      if (matched) {
        this.setActiveThumb(matched);
        this.swapImageBySrc(matched.dataset.pdpSrc, matched.dataset.pdpSrcset);
      } else {
        this.swapImageBySrc(src);
      }
    },

    filterByMediaIds: function (ids) {
      if (!this.thumbs || !this.thumbs.length) return;
      if (!ids || !ids.length) {
        this.thumbs.forEach(function (t) { t.style.display = ''; });
        return;
      }
      var idSet = {};
      ids.forEach(function (id) { idSet[String(id)] = true; });

      var self = this;
      var firstVisible = null;
      var activeIsHidden = false;
      this.thumbs.forEach(function (t) {
        var show = !!idSet[t.dataset.pdpThumb];
        t.style.display = show ? '' : 'none';
        if (show && !firstVisible) firstVisible = t;
        if (!show && t.classList.contains('pdp-gallery__thumb--active')) activeIsHidden = true;
      });

      if (activeIsHidden && firstVisible) {
        self.setActiveThumb(firstVisible);
        self.swapImageBySrc(firstVisible.dataset.pdpSrc, firstVisible.dataset.pdpSrcset);
      }
    }
  };

  /* ── Price ── */
  var FyrePrice = {
    el: null,
    init: function (sectionId) { this.el = document.getElementById('pdp-price-' + sectionId); },
    update: function (variant) {
      if (!this.el) return;
      // Custom Caliper Color adds the matched "Custom Caliper Paint" variant's own price on top —
      // read live off that product so a later Stage/piston repricing needs no code change here.
      var surcharge = FyreProductForm.getCustomColorSurcharge ? FyreProductForm.getCustomColorSurcharge() : 0;
      // A pad swap moves the price by the DIFFERENCE from the pad the kit already includes — up or down.
      // Zero on non-builder products, where the pad is a separate paid line rather than a kit substitution.
      surcharge += FyrePadPicker.getDeltaCents ? FyrePadPicker.getDeltaCents() : 0;
      var price = variant.price + surcharge;
      var comparePrice = variant.compare_at_price ? variant.compare_at_price + surcharge : variant.compare_at_price;
      if (comparePrice > price) {
        this.el.innerHTML =
          '<s class="pdp-price__compare font-secondary">' + formatMoney(comparePrice) + '</s>' +
          '<span class="pdp-price__sale font-secondary">' + formatMoney(price) + '</span>';
      } else {
        this.el.innerHTML =
          '<span class="pdp-price__regular font-secondary">' + formatMoney(price) + '</span>';
      }
    }
  };

  /* ── Product Form (variant selection) ── */
  var FyreProductForm = {
    productData: null, selects: null, pills: null, variantInput: null, skuEl: null, sectionId: null,

    init: function (sectionId) {
      var form = document.getElementById('pdp-form-' + sectionId);
      if (!form) return;
      var jsonEl = document.getElementById('pdp-product-json-' + sectionId);
      if (!jsonEl) return;
      try { this.productData = JSON.parse(jsonEl.textContent); } catch (e) { return; }

      // RT Product Feed gate. `product | json` above carries no metafields, so the feed cannot be re-read here —
      // the section ships the gate's verdict as a list of ids instead. Dropping them from productData covers every
      // consumer at once (getSelectedVariant, getCartVariant), so a gated SKU can never be selected or carted.
      // An unusable payload must not restore the full list: the Liquid pickers drop gated VALUES, but a gated
      // cross-option COMBINATION survives them, so falling back to every variant re-opens the hole this closes.
      // Empty the list instead — every consumer already treats "no variant" as unavailable.
      var hiddenEl = document.getElementById('pdp-rt-hidden-variants-' + sectionId);
      if (this.productData.variants) {
        try {
          if (!hiddenEl) throw new Error('rt-feed gate payload missing');
          var hiddenIds = JSON.parse(hiddenEl.textContent);
          if (!Array.isArray(hiddenIds)) throw new Error('rt-feed gate payload is not an array');
          if (hiddenIds.length) {
            var hiddenSet = {};
            hiddenIds.forEach(function (id) { hiddenSet[String(id)] = true; });
            this.productData.variants = this.productData.variants.filter(function (v) { return !hiddenSet[String(v.id)]; });
          }
        } catch (e) {
          if (window.console) console.error('[fyre-pdp] RT Product Feed gate unusable, disabling purchase:', e);
          this.productData.variants = [];
        }
      }

      var formId = 'pdp-form-' + sectionId;
      var section = document.querySelector('[data-pdp-section="' + sectionId + '"]') || document;
      this.selects = section.querySelectorAll('[data-pdp-option-select][form="' + formId + '"]');
      this.pills = section.querySelectorAll('[data-pdp-option-radio][form="' + formId + '"]');
      this.variantInput = form.querySelector('[name="id"]');
      this.skuEl = section.querySelector('[data-pdp-sku]');
      this.customColorPriceEl = section.querySelector('[data-custom-color-price]');
      this.sectionId = sectionId;

      var self = this;
      this.selects.forEach(function (s) { s.addEventListener('change', function () { self.onVariantChange(); }); });
      this.pills.forEach(function (p) { p.addEventListener('change', function () { self.onVariantChange(); }); });

      var customPanel = section.querySelector('[data-custom-color-panel]');
      var isCustomActive = false;
      if (customPanel) {
        var allInputs = customPanel.querySelectorAll('input');
        var logoRadios = [];
        var caliperRadios = [];
        var paintField = null;
        allInputs.forEach(function (i) {
          if (i.type === 'radio' && i.name.indexOf('Logo Color') !== -1) logoRadios.push(i);
          else if (i.type === 'radio' && i.name.indexOf('Caliper Color') !== -1) caliperRadios.push(i);
          else if (i.type === 'text' && i.name.indexOf('Paint Code') !== -1) paintField = i;
        });

        var logoLabelEl = section.querySelector('[data-custom-color-label="logo"]');
        var caliperLabelEl = section.querySelector('[data-custom-color-label="caliper"]');

        var caliperJsonEl = document.getElementById('pdp-custom-caliper-paint-' + sectionId);
        var caliperPaintData = null;
        if (caliperJsonEl) { try { caliperPaintData = JSON.parse(caliperJsonEl.textContent); } catch (e) { /* empty */ } }
        self.caliperPaintData = caliperPaintData;

        function isMetallicSelected() {
          var caliperChecked = caliperRadios.find(function (r) { return r.checked; });
          var text = ((caliperChecked ? caliperChecked.value : '') + ' ' + (paintField ? paintField.value : '')).toLowerCase();
          return text.indexOf('metallic') !== -1;
        }

        // Looks up the Custom Caliper Paint variant for the current piston count + stage (Standard/Stage 3),
        // regardless of whether Custom is the ACTIVE selection right now — this is the "if you pick Custom,
        // it'll cost +$X" preview, same as every other swatch's always-visible price_diff badge.
        function findCaliperPaintVariant() {
          if (!caliperPaintData || !caliperPaintData.pistonMatch) return null;
          var stage = isMetallicSelected() ? 'Stage 3' : 'Standard';
          return (caliperPaintData.variants || []).find(function (v) {
            return v.calipers === caliperPaintData.pistonMatch && v.stage === stage;
          }) || null;
        }

        // Swatch preview price: shown whether or not Custom is currently selected.
        self.getCustomColorSwatchSurcharge = function () {
          var m = findCaliperPaintVariant();
          return m ? m.price : 0;
        };

        // Single source of truth for "which Custom Caliper Paint variant is ACTUALLY being charged right
        // now" — gated on Custom being the live selection. The main price, ATC button price, and the actual
        // /cart/add.js item all read this (never the preview above), so they can never disagree, and it stays
        // live off the real variant price (never a hardcoded $500).
        self.getMatchedCaliperPaintVariant = function () {
          if (!isCustomActive) return null;
          return findCaliperPaintVariant();
        };

        self.getCustomColorSurcharge = function () {
          var m = self.getMatchedCaliperPaintVariant();
          return m ? m.price : 0;
        };

        function updateColorLabels() {
          var logoChecked = logoRadios.find(function (r) { return r.checked; });
          if (logoLabelEl) logoLabelEl.textContent = logoChecked ? ': ' + logoChecked.value : '';
          var caliperChecked = caliperRadios.find(function (r) { return r.checked; });
          if (caliperLabelEl) caliperLabelEl.textContent = caliperChecked ? ': ' + caliperChecked.value : '';
        }

        function defaultSelections() {
          var logoChecked = logoRadios.some(function (r) { return r.checked; });
          if (!logoChecked && logoRadios.length) logoRadios[0].checked = true;
          var paintVal = paintField ? paintField.value.trim() : '';
          if (!paintVal) {
            var caliperChecked = caliperRadios.some(function (r) { return r.checked; });
            if (!caliperChecked && caliperRadios.length) caliperRadios[0].checked = true;
          }
          updateColorLabels();
        }

        var customToggleEl = section.querySelector('[data-custom-color-toggle]');
        var customOptionGroup = customToggleEl ? customToggleEl.closest('.pdp-option') : null;
        var colorIdx = self.getColorOptionIndex();
        function toggleCustomPanel() {
          // Custom is an ADD-ON CHECKBOX, not a colour option value (see the add-on note in
          // sections/fyre-template-product.liquid). The legacy option-value form is still honoured so a
          // product that has not been migrated keeps working, but the checkbox is the path every builder
          // umbrella takes — including the ones whose colour option never carried a Custom value at all.
          isCustomActive = !!(customToggleEl && customToggleEl.checked) || Array.prototype.some.call(
            section.querySelectorAll('[data-pdp-option-radio]:checked'),
            function (r) {
              var h = r.value.toLowerCase().replace(/\s+/g, '-');
              return h === 'custom' || h === 'custom-color';
            }
          );
          // Read by getCartVariant (standalone PDPs) and fyre-resolve.js applyResolution (umbrellas): with
          // Custom on, the cart line swaps to the highest-inventory colour sibling of the selected config.
          self.customPaintActive = isCustomActive;
          // One or the other on screen: a checked colour swatch stays checked underneath (variant selection and
          // backing resolution still need a colour axis) but the group renders Custom as the selection.
          if (customOptionGroup) customOptionGroup.classList.toggle('pdp-option--custom-active', !!(customToggleEl && customToggleEl.checked));
          customPanel.classList.toggle('is-visible', isCustomActive);
          allInputs.forEach(function (i) { i.disabled = !isCustomActive; });
          if (isCustomActive) defaultSelections();
          // Selecting/deselecting Custom changes the surcharge (0 <-> matched variant price) — refresh
          // every price display (swatch label, main price, ATC button), not just the panel visibility.
          self.updateState(self.getSelectedVariant());
        }

        self.pills.forEach(function (p) {
          // the toggle's own change event re-runs toggleCustomPanel, so a release needs no second call here
          p.addEventListener('click', function () { releaseCustomForColorPick(customToggleEl, p, colorIdx); });
          p.addEventListener('change', function (e) { releaseCustomForColorChange(e, customToggleEl, p, colorIdx); toggleCustomPanel(); });
        });
        // The add-on checkbox is NOT one of self.pills (those are the variant-selecting option radios), so it
        // needs its own listener or ticking it would show no panel and add no paint line.
        if (customToggleEl) customToggleEl.addEventListener('change', toggleCustomPanel);
        toggleCustomPanel();

        logoRadios.forEach(function (r) {
          r.addEventListener('change', updateColorLabels);
        });
        caliperRadios.forEach(function (r) {
          r.addEventListener('change', function () {
            if (paintField) paintField.value = '';
            updateColorLabels();
            // A different swatch can flip Standard <-> Stage 3 without changing the outer variant pill,
            // so this doesn't go through onVariantChange — refresh prices explicitly.
            self.updateState(self.getSelectedVariant());
          });
        });

        if (paintField) {
          paintField.addEventListener('input', function () {
            if (paintField.value.trim()) {
              caliperRadios.forEach(function (r) { r.checked = false; });
              if (caliperLabelEl) caliperLabelEl.textContent = '';
            } else {
              if (caliperRadios.length) caliperRadios[0].checked = true;
              updateColorLabels();
            }
            self.updateState(self.getSelectedVariant());
          });
        }
      }

      this.updateState(this.getSelectedVariant());
    },

    refreshCustomColorPrice: function () {
      if (!this.customColorPriceEl) return;
      // Preview price — shows under the CUSTOM swatch regardless of whether it's currently selected,
      // same as every other swatch's always-visible price_diff badge.
      var surcharge = this.getCustomColorSwatchSurcharge ? this.getCustomColorSwatchSurcharge() : 0;
      if (surcharge > 0) {
        this.customColorPriceEl.textContent = '+' + formatMoneyTrim(surcharge);
        this.customColorPriceEl.hidden = false;
      } else {
        this.customColorPriceEl.hidden = true;
      }
    },

    getSelectedVariant: function () {
      if (!this.productData) return null;
      if (!this.selects.length && !this.pills.length) return this.productData.variants[0];
      var optCount = this.productData.options ? this.productData.options.length : 3;
      var opts = new Array(optCount).fill(null);
      this.selects.forEach(function (s) {
        var idx = parseInt(s.dataset.optionIndex, 10);
        if (!isNaN(idx)) opts[idx] = s.value;
      });
      this.pills.forEach(function (p) {
        if (p.checked) {
          var idx = parseInt(p.dataset.optionIndex, 10);
          if (!isNaN(idx)) opts[idx] = p.value;
        }
      });
      return this.productData.variants.find(function (v) {
        return v.options.every(function (o, i) { return opts[i] === null || o === opts[i]; });
      }) || null;
    },

    onVariantChange: function () { this.updateState(this.getSelectedVariant()); },

    getColorOptionIndex: function () {
      if (!this.productData || !this.productData.options) return -1;
      var idx = -1;
      this.productData.options.forEach(function (name, i) {
        var lower = String(name).toLowerCase();
        if (lower.indexOf('finish') !== -1 || lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) idx = i;
      });
      return idx;
    },

    isCustomColorValue: function (value) {
      var h = String(value || '').toLowerCase().replace(/\s+/g, '-');
      return h === 'custom' || h === 'custom-color';
    },

    // Runs when the Custom paint add-on is active, or (legacy) when the selected variant carries a blank-SKU
    // Custom colour value. Custom Color variants ship with a blank SKU — they're a pricing/UI placeholder, not a real,
    // fulfillable stock item (the actual paint job rides on the separate Custom Caliper Paint addon
    // line, see FyreATC.submit). The cart line itself must carry a real color variant instead, so pick
    // whichever sibling color (same size/rotor/etc, every other option identical) has the most on-hand
    // inventory. Returns null if Custom is selected but no in-stock sibling exists — callers must treat
    // that as unavailable rather than falling back to the no-SKU variant.
    getCartVariant: function (variant) {
      if (!variant) return variant;
      var colorIdx = this.getColorOptionIndex();
      if (colorIdx === -1 || !variant.options) return variant;
      // Custom is the add-on toggle (customPaintActive) — the selected colour swatch underneath is not what ships.
      // The legacy Custom option VALUE is still honoured for any unmigrated product.
      if (!this.customPaintActive && !this.isCustomColorValue(variant.options[colorIdx])) return variant;

      var self = this;
      var best = null;
      (this.productData.variants || []).forEach(function (v) {
        if (self.isCustomColorValue(v.options[colorIdx]) || !v.available) return;
        var matches = v.options.every(function (o, i) { return i === colorIdx || o === variant.options[i]; });
        if (!matches) return;
        var qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 0;
        if (!best || qty > best.qty) best = { variant: v, qty: qty };
      });
      return best ? best.variant : null;
    },

    updateState: function (variant) {
      if (!variant) { FyreATC.setUnavailable(); return; }
      // On builder umbrellas the resolver (fyre-resolve.js) is the SOLE owner of the cart form's [name=id] —
      // it writes the resolved REAL backing variant SKU. FyreProductForm must NOT overwrite it with the dead
      // umbrella variant, or the fail-closed ATC guard silently blocks the cart (dual-writer race → dead ATC,
      // no error logs). WHY: the umbrella is a data-less shell; its own variant is never purchasable.
      var cartVariant = this.getCartVariant(variant);
      if (this.variantInput && !document.querySelector('[data-fyre-resolve]')) {
        this.variantInput.value = cartVariant ? cartVariant.id : '';
      }

      // The resolver owns the PRICE and the SKU LABEL on builder umbrellas for the same reason it owns
      // [name=id] above: the umbrella is a data-less shell, so writing its own variant here replaces the
      // resolved backing's figures with the shell's. Measured on a 380mm config — resolved $5,494, then a
      // paint-panel colour pick rewrote it to the umbrella base plus the surcharge, $4,894, six hundred
      // dollars under what the cart charges. Announce the surcharge instead and let the resolver re-render,
      // exactly as a pad change does through 'fyre:pad-delta'.
      var resolverOwned = !!document.querySelector('[data-fyre-resolve]');
      if (resolverOwned) {
        try { window.dispatchEvent(new CustomEvent('fyre:paint-surcharge')); } catch (e) {}
      } else {
        FyrePrice.update(variant);
      }
      FyreATC.update(variant, cartVariant);
      this.refreshCustomColorPrice();

      if (this.skuEl && !resolverOwned) this.skuEl.textContent = variant.sku ? 'SKU: ' + variant.sku : '';

      // On builder umbrellas the resolver owns the note's SKU (via the 'fyre:resolved' event) — it feeds the REAL
      // resolved backing SKU. FyreProductForm must not overwrite it with the null/umbrella SKU.
      if (typeof FyreFitmentNote !== 'undefined' && !document.querySelector('[data-fyre-resolve]')) FyreFitmentNote.setSku(variant.sku);

      var section = document.querySelector('[data-pdp-section="' + this.sectionId + '"]');
      if (section && variant.options) {
        variant.options.forEach(function (value, idx) {
          var el = section.querySelector('[data-pdp-option-value="' + idx + '"]');
          if (el) el.textContent = value;
        });

      }

      if (variant.featured_media && variant.featured_media.preview_image) {
        FyreGallery.swapToVariantImage(variant.featured_media.preview_image.src);
      }

      var pData = this.productData;
      if (pData && pData.options && variant.options && FyreGallery.thumbs && FyreGallery.thumbs.length) {
        var colorIdx = -1;
        pData.options.forEach(function (name, idx) {
          var lower = name.toLowerCase();
          if (lower.indexOf('finish') !== -1 || lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) {
            colorIdx = idx;
          }
        });
        if (colorIdx !== -1) {
          var colorSlug = variant.options[colorIdx].toLowerCase();
          var matchedIds = [];
          FyreGallery.thumbs.forEach(function (t) {
            if ((t.dataset.pdpSrc || '').toLowerCase().indexOf(colorSlug) !== -1) {
              matchedIds.push(t.dataset.pdpThumb);
            }
          });
          FyreGallery.filterByMediaIds(matchedIds);
        }
      }
    }
  };
  // fyre-resolve.js is the last price writer on builder umbrellas and reads the paint surcharge from here.
  // Distinct from the FyreProductForm custom-element class in fyre-form-product.js, which owns that global name.
  window.FyrePdpForm = FyreProductForm;

  /* ── Quantity ── */
  var FyreQty = {
    init: function () {
      var input = document.querySelector('[data-pdp-qty]');
      if (!input) return;
      var dec = document.querySelector('[data-pdp-qty-dec]');
      var inc = document.querySelector('[data-pdp-qty-inc]');
      if (dec) dec.addEventListener('click', function () { var v = parseInt(input.value) || 1; if (v > 1) input.value = v - 1; });
      if (inc) inc.addEventListener('click', function () { var v = parseInt(input.value) || 1; input.value = v + 1; });
    },
    getValue: function () {
      var input = document.querySelector('[data-pdp-qty]');
      return input ? Math.max(1, parseInt(input.value) || 1) : 1;
    }
  };

  /* ── Warranty ── */
  var FyreWarranty = {
    selectedVariantId: null,
    init: function () {
      var radios = document.querySelectorAll('[data-warranty-radio]');
      if (!radios.length) return;
      document.querySelectorAll('[data-warranty-info]').forEach(function (info) {
        info.addEventListener('click', function (e) { e.stopPropagation(); });
        info.addEventListener('mousedown', function (e) { e.stopPropagation(); });
      });
      var self = this;
      radios.forEach(function (radio) {
        var label = radio.closest('label');
        if (label) label.addEventListener('mousedown', function () { radio._wasChecked = radio.checked; });
        radio.addEventListener('click', function () {
          if (radio._wasChecked) { radio.checked = false; self.selectedVariantId = null; }
          else { self.selectedVariantId = radio.value; }
          radio._wasChecked = false;
        });
      });
    },
    getSelected: function () { return this.selectedVariantId; }
  };

  /* ── Pad Picker ── */
  // On BUILDER umbrellas the product-level content.pad_products is a UNION across every backing/diameter, so its
  // compound pills can duplicate (e.g. "FM1000 x3"). The resolver (fyre-resolve.js) broadcasts the
  // RESOLVED backing's own pads via 'fyre:pad-options'; we rebuild the pills from that (shape-correct, one per
  // compound). Non-builder products have no resolver → the static Liquid pills stand and ARE the source of truth.
  var FyrePadPicker = {
    picker: null, labelEl: null, pillsEl: null, staticHTML: '',
    isBuilder: false, resolved: false, defaultPad: null,

    init: function () {
      var picker = document.querySelector('[data-pdp-pad-picker]');
      if (!picker) return;
      this.picker = picker;
      this.labelEl = picker.querySelector('[data-pdp-pad-value]');
      this.pillsEl = picker.querySelector('.pdp-option__pills');
      this.staticHTML = this.pillsEl ? this.pillsEl.innerHTML : '';
      this.isBuilder = !!document.querySelector('[data-fyre-resolve]');
      // builder: the static pills are the umbrella UNION — NOT harvestable until the resolver paints.
      // non-builder: the static pills are correct → resolved from the start.
      this.resolved = !this.isBuilder;
      // A builder umbrella's static pills are the cross-diameter UNION — offering them before a backing resolves
      // invites a choice we then silently replace. Stay hidden until the resolver paints the real shape.
      if (this.isBuilder) this.setHidden(true);
      // Non-builder products carry their own default in Liquid; builders get it per-backing off the resolver.
      if (!this.isBuilder) this.defaultPad = this.readStaticDefault();

      var self = this;
      // DELEGATE the change handler to the container so it survives pill rebuilds (per-node binding would die).
      picker.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'pad_compound') { self.syncLabel(); self.emitDelta(); }
      });
      this.syncLabel();

      if (this.isBuilder) {
        window.addEventListener('fyre:pad-options', function (e) { self.onPadOptions(e.detail || {}); });
        // recover a pad dispatch that fired before this listener existed (init-order race): ask the resolver to
        // replay the current backing's pads. No-op if nothing is resolved yet (static pills stay).
        try { window.dispatchEvent(new CustomEvent('fyre:pad-options-request')); } catch (e) {}
      }
    },

    // "Brake Pads … | Brembo FM1000 Compound, 18mm" -> "BREMBO FM1000" (pill/label text + preserve-selection key).
    compoundOf: function (title) {
      var parts = String(title || '').split('| ');
      var c = parts.length > 1 ? parts[parts.length - 1] : parts[0];
      return c.split(' Compound')[0].trim().toUpperCase();
    },
    esc: function (s) {
      return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },
    checkedRadio: function () { return this.picker ? this.picker.querySelector('[name="pad_compound"]:checked') : null; },
    syncLabel: function () {
      if (!this.labelEl) return;
      var r = this.checkedRadio();
      if (r) this.labelEl.textContent = this.compoundOf(r.value);
    },

    setHidden: function (hidden) { if (this.picker) this.picker.style.display = hidden ? 'none' : ''; },

    // ── Net price effect of a pad swap ──────────────────────────────────────────────────────────
    // The kit price ALREADY includes specs.default_pad_set, and a swap credits that pad back (the $0
    // pad-swap marker line is what the discount Function reads). So what the buyer actually pays for a
    // different compound is the DIFFERENCE between the two pads, never the chosen pad's own price.
    // Same arithmetic and the same +/- money format as the B2B quick-order form (formatDelta in
    // algolia-b2b-infinite-hits.js), so a dealer and a retail shopper are quoted the same number.
    //
    // Builder-only by construction: padIncludedInKit in FyreATC.submit gates the credit on
    // [data-fyre-resolve], so on a non-builder product the pad is an ordinary paid line with no credit
    // and no delta to show. Showing one there would promise a discount that never fires.
    padDelta: 0,

    defaultPadPrice: function () {
      var d = this.defaultPad;
      if (!this.isBuilder || !d || d.price === '' || d.price == null) return null;
      var n = Number(d.price);
      return isFinite(n) ? n : null;
    },

    // dollars, signed. null when we cannot prove a baseline — the caller then renders NO label rather
    // than a delta computed against a missing default, which would read as a real price to the buyer.
    deltaOf: function (padPrice) {
      var base = this.defaultPadPrice();
      if (base === null) return null;
      var n = Number(padPrice);
      if (!isFinite(n)) return null;
      return n - base;
    },

    formatDelta: function (dollars) {
      var cents = Math.round(dollars * 100);
      return (cents < 0 ? '-' : '+') + formatMoneyTrim(Math.abs(cents));
    },

    // Recompute from the checked radio and tell the price writers. fyre-resolve.js owns .pdp-price on a
    // resolved builder umbrella, so the delta reaches it as an event rather than a cross-module call.
    emitDelta: function () {
      var d = 0;
      if (this.isBuilder && this.resolved) {
        var r = this.checkedRadio();
        var v = r ? this.deltaOf(r.dataset.padPrice) : null;
        if (v !== null) d = v;
      }
      if (d === this.padDelta) return;
      this.padDelta = d;
      try { window.dispatchEvent(new CustomEvent('fyre:pad-delta', { detail: { delta: d } })); } catch (e) {}
    },

    // cents, for FyrePrice.update — which composes the displayed price from variant + add-ons.
    getDeltaCents: function () { return Math.round((this.padDelta || 0) * 100); },

    // resolver -> render per state (fyre-resolve.js dispatchPad):
    //   'pending' -> a new backing is resolving; block harvest until its pads paint (don't leave `resolved` true
    //                across a backing switch — else getSelected() harvests the PRIOR backing's pad)
    //   'ready'   -> paint the resolved backing's shape-correct pads
    //   'empty'   -> resolved backing has NO pads: hide the picker (don't show the umbrella union as fake offers)
    //   'failed'  -> pad fetch failed after retries: hide the picker (no false checked pill), harvest stays null
    //   'static'  -> pre-resolution / no backing: restore the static union pills
    onPadOptions: function (detail) {
      if (!this.pillsEl) return;
      var state = detail && detail.state;
      if (this.isBuilder) this.defaultPad = detail && detail.defaultPad ? detail.defaultPad : null;
      if (state === 'pending') { this.resolved = false; this.emitDelta(); return; } // block stale harvest through the fetch window
      if (state === 'empty' || state === 'failed') { this.resolved = false; this.setHidden(true); this.syncLabel(); this.emitDelta(); return; } // kit BOM still includes its pad
      var pads = detail && detail.pads;
      if (state === 'static' || !pads || !pads.length) { // pre-resolution / no backing -> the umbrella union pills
        this.setHidden(this.isBuilder); // builders stay hidden until a backing resolves; the union is not an offer

        this.pillsEl.innerHTML = this.staticHTML;
        this.resolved = !this.isBuilder; // non-builder: static pills ARE the source of truth (harvestable); builder: block until resolve
        this.syncLabel();
        this.emitDelta();
        return;
      }
      this.setHidden(false);
      var self = this;
      var r = this.checkedRadio();
      var prev = r ? this.compoundOf(r.value) : null;
      // Preserve the prior compound if still offered AND in stock; else the first in-stock pad; else pill[0]
      // (every compound OOS — still need exactly one checked radio). Never default onto an OOS compound while
      // a stocked one exists — that silent sold-out default is what let an unavailable pad reach the cart.
      var keepIdx = -1;
      if (prev) { for (var k = 0; k < pads.length; k++) { if (self.compoundOf(pads[k].title) === prev && pads[k].available) { keepIdx = k; break; } } }
      if (keepIdx === -1) { for (var j = 0; j < pads.length; j++) { if (pads[j].available) { keepIdx = j; break; } } }
      if (keepIdx === -1) keepIdx = 0;
      // Every pill states its net effect in the same units, the kit's own pad included — it reads +$0
      // rather than a word, so the whole row stays one comparable scale at a glance.
      function deltaLabel(pad) {
        // CATALOG-MODE: pad prices are hidden while the store is catalog-only — dealers read retail pad
        // prices as their own. Restore by deleting this line.
        return '';
        var d = self.deltaOf(pad.price);
        if (d === null) return '';
        return '<span class="pdp-option__pill-price">' + self.esc(self.formatDelta(d)) + '</span>';
      }
      var html = '';
      pads.forEach(function (pad, i) {
        html += '<label class="pdp-option__pill' + (pad.available ? '' : ' is-unavailable') + '">' +
          '<input type="radio" name="pad_compound"' +
          ' value="' + self.esc(pad.title) + '"' +
          ' data-pad-sku="' + self.esc(pad.sku) + '"' +
          ' data-pad-price="' + self.esc(pad.price) + '"' +
          ' data-pad-variant-id="' + self.esc(pad.variantId) + '"' +
          (i === keepIdx ? ' checked' : '') + (pad.available ? '' : ' disabled') + '>' +
          '<span class="pdp-option__pill-text"><span>' + self.esc(self.compoundOf(pad.title)) + '</span>' + deltaLabel(pad) + '</span>' +
          '</label>';
      });
      this.pillsEl.innerHTML = html;
      // One pad for this shape is a complete answer, not an empty control (P1012.18 has exactly one). Keep the
      // radio so getSelected() can still harvest the SKU; hide the row so it does not read as a broken chooser.
      this.pillsEl.hidden = pads.length === 1;
      if (pads.length === 1) this.pillsEl.setAttribute('data-pad-locked', '');
      else this.pillsEl.removeAttribute('data-pad-locked');
      this.resolved = true;
      this.syncLabel();
      this.emitDelta();
    },

    // Non-builder products publish specs.default_pad_set as JSON from the Liquid section; builders get it from
    // the resolver, per backing. Either way it names the pad ALREADY PRICED INTO the kit — the swap baseline.
    readStaticDefault: function () {
      var el = document.getElementById('pdp-default-pad');
      if (!el) return null;
      try { var d = JSON.parse(el.textContent || 'null'); return (d && d.sku) ? d : null; } catch (e) { return null; }
    },

    getDefault: function () { return this.defaultPad; },

    // A pad is "the default" when its SKU matches specs.default_pad_set. With no known default we cannot tell a
    // swap from a no-op, so report false and let the caller emit no swap lines rather than a wrong credit.
    isDefaultSelected: function () {
      var d = this.defaultPad, sel = this.getSelected();
      if (!d || !d.sku || !sel || !sel.sku) return false;
      return String(d.sku) === String(sel.sku);
    },

    getSelected: function () {
      // On a builder umbrella, don't harvest the static UNION pill before the resolver paints the resolved
      // backing's pads — that SKU is cross-diameter (wrong shape). Return null until resolved; FyreATC.submit
      // already null-guards `pad`, so the pad property is simply omitted for that (sub-second) window.
      if (this.isBuilder && !this.resolved) return null;
      var radio = this.checkedRadio() || document.querySelector('[name="pad_compound"]:checked');
      if (!radio) return null;
      return {
        name: radio.value,
        sku: radio.dataset.padSku || '',
        variantId: radio.dataset.padVariantId ? parseInt(radio.dataset.padVariantId, 10) : null
      };
    }
  };

  /* ── Add to Cart ── */
  var FyreATC = {
    btn: null, errorEl: null, errorTimer: null, sectionId: null, requiresFitment: false, isResolver: false, hasFitment: false, fitPending: false, fitNoMatch: false, verifyFailed: false, hasVehicleFitment: false, verifyTimer: null,

    init: function (sectionId) {
      this.sectionId = sectionId;
      this.btn = document.querySelector('[data-pdp-atc]');
      if (!this.btn) return;
      this.errorEl = document.querySelector('[data-atc-error]');
      this.requiresFitment = this.btn.hasAttribute('data-requires-fitment');
      // Resolver pages (builder umbrellas) manage the ATC button directly — stay out entirely.
      this.isResolver = !!document.querySelector('[data-fyre-resolve]');

      if (this.requiresFitment && !this.isResolver) {
        // Vehicle-grain products (moto/PS) get their verdict asynchronously from
        // fyre-pdp-vehicle-compat.js; arm a watchdog so a never-arriving verdict surfaces an honest
        // "couldn't verify" state instead of a silently-disabled button forever.
        var fbar = document.querySelector('[data-pdp-fitment]');
        this.hasVehicleFitment = !!(fbar && fbar.getAttribute('data-has-vehicle-fitment') === 'true');
        var vehicle = null;
        try {
          vehicle = window.FitmentStorage ? window.FitmentStorage.get()
            : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null');
        } catch (e) { /* empty */ }
        this.hasFitment = false;
        this.fitPending = !!vehicle;
        this.fitNoMatch = false;
        this.verifyFailed = false;
        this.applyFitmentState();
        this.armVerifyWatchdog();

        var self = this;
        window.addEventListener('fitment:changed', function (e) {
          self.clearVerifyWatchdog();
          self.hasFitment = false;
          self.fitNoMatch = false;
          self.verifyFailed = false;
          self.fitPending = !!e.detail;
          self.applyFitmentState();
          self.armVerifyWatchdog();
        });
        window.addEventListener('fitment:verified', function (e) {
          self.clearVerifyWatchdog();
          var fits = !!(e.detail && e.detail.fits);
          self.hasFitment = fits;
          self.fitPending = false;
          self.fitNoMatch = !fits;
          self.verifyFailed = false;
          self.applyFitmentState();
        });
        window.addEventListener('fitment:verify-unavailable', function () {
          self.clearVerifyWatchdog();
          self.hasFitment = false;
          self.fitPending = false;
          self.fitNoMatch = false;
          self.verifyFailed = true;
          self.applyFitmentState();
        });
      }

      var self = this;
      this.btn.addEventListener('click', function (e) { e.preventDefault(); self.submit(); });
    },

    applyFitmentState: function () {
      if (!this.btn || !this.requiresFitment || this.isResolver) return;
      var textEl = this.btn.querySelector('[data-atc-text]');
      var subEl = this.btn.querySelector('[data-atc-sub]');
      if (this.hasFitment) {
        this.btn.disabled = false;
        this.btn.classList.remove('pdp-atc-btn--needs-fitment');
        if (textEl) textEl.textContent = 'ADD TO CART';
        if (subEl) subEl.textContent = 'IN STOCK — SHIPS WITHIN 2 BUSINESS DAYS';
      } else if (this.fitPending) {
        // Vehicle set, async fit check in flight — keep button appearance normal but disabled.
        this.btn.disabled = true;
        this.btn.classList.remove('pdp-atc-btn--needs-fitment');
        if (textEl) textEl.textContent = 'ADD TO CART';
        if (subEl) subEl.textContent = 'IN STOCK — SHIPS WITHIN 2 BUSINESS DAYS';
      } else if (this.fitNoMatch) {
        // Vehicle is set but does not fit this product.
        this.btn.disabled = true;
        this.btn.classList.add('pdp-atc-btn--needs-fitment');
        if (textEl) textEl.textContent = 'DOES NOT FIT YOUR VEHICLE';
        if (subEl) subEl.textContent = 'SELECT A COMPATIBLE VEHICLE ABOVE';
      } else if (this.verifyFailed) {
        // Async fitment verification failed or never arrived (fyre-pdp-vehicle-compat.js). Fail
        // CLOSED — never allow a possibly-wrong-fit brake purchase — but honestly and recoverably.
        this.btn.disabled = true;
        this.btn.classList.add('pdp-atc-btn--needs-fitment');
        if (textEl) textEl.textContent = "COULDN'T VERIFY FITMENT";
        if (subEl) subEl.textContent = 'PLEASE RELOAD TO TRY AGAIN';
      } else {
        // No vehicle set at all.
        this.btn.disabled = true;
        this.btn.classList.add('pdp-atc-btn--needs-fitment');
        if (textEl) textEl.textContent = 'SELECT VEHICLE FIRST';
        if (subEl) subEl.textContent = 'SET FITMENT ABOVE TO ADD TO CART';
      }
    },

    // Watchdog for vehicle-grain products only: their verdict is dispatched asynchronously by
    // fyre-pdp-vehicle-compat.js. If it never arrives (paged fetch hung, or the compat panel/section
    // is absent so no owner ran), flip to the honest verifyFailed state instead of a silently-disabled
    // "ADD TO CART" forever. Cleared the moment a verdict (verified/verify-unavailable) or a new
    // fitment:changed arrives.
    armVerifyWatchdog: function () {
      if (!this.hasVehicleFitment || !this.fitPending) return;
      this.clearVerifyWatchdog();
      var self = this;
      this.verifyTimer = setTimeout(function () {
        if (self.fitPending) {
          // If the verdict owner never loaded (Product Info section removed/reordered, or asset 404),
          // say so — otherwise a catalog-wide "COULDN'T VERIFY" brick is silent and undiagnosable.
          if (!window.__fyreVehicleVerdictOwner) {
            console.error('[fyre-pdp] fitment verdict owner absent — fyre-pdp-vehicle-compat.js did not run '
              + '(Product Info section removed/reordered or asset failed to load). moto/PS ATC cannot verify fitment.');
          }
          self.verifyFailed = true; self.fitPending = false; self.applyFitmentState();
        }
      }, 9000);
    },

    clearVerifyWatchdog: function () {
      if (this.verifyTimer) { clearTimeout(this.verifyTimer); this.verifyTimer = null; }
    },

    update: function (variant, cartVariant) {
      if (!this.btn) return;
      var priceEl = this.btn.querySelector('[data-atc-price]');
      if (priceEl) {
        var surcharge = FyreProductForm.getCustomColorSurcharge ? FyreProductForm.getCustomColorSurcharge() : 0;
        priceEl.textContent = formatMoney(variant.price + surcharge);
      }
      if (this.isResolver) return;
      if (this.requiresFitment && !this.hasFitment) return;
      // cartVariant is the variant actually added to cart (see FyreProductForm.getCartVariant) — when
      // Custom Color is picked but every real sibling color is out of stock, it's null, and the button
      // must go SOLD OUT rather than let the no-SKU Custom variant itself get purchased.
      var purchasable = arguments.length > 1 ? !!(cartVariant && cartVariant.available) : variant.available;
      this.btn.disabled = !purchasable;
      var textEl = this.btn.querySelector('[data-atc-text]');
      if (textEl) textEl.textContent = purchasable ? 'ADD TO CART' : 'SOLD OUT';
    },

    setUnavailable: function () {
      if (!this.btn) return;
      this.btn.disabled = true;
      var textEl = this.btn.querySelector('[data-atc-text]');
      if (textEl) textEl.textContent = 'UNAVAILABLE';
    },

    submit: function () {
      // Builder umbrellas (isResolver) manage the ATC button's enabled state directly via fyre-resolve.js's
      // fail-closed setAtc() — the button is clickable ONLY after a clean resolve. On resolver pages this module
      // never registers the `fitment:verified` listener (init() skips it when isResolver at :404), so `hasFitment`
      // stays undefined and this gate would block EVERY submit. The isResolver exemption guards
      // init()/update()/applyFitmentState() and must hold here too, or builder cart-adds die silently.
      // Exempt resolvers; the resolver owns the gate.
      if (this.requiresFitment && !this.hasFitment && !this.isResolver) return;

      var form = document.getElementById('pdp-form-' + this.sectionId);
      if (!form) return;
      var variantId = parseInt(form.querySelector('[name="id"]').value);
      // Custom Color with every real sibling color out of stock: getCartVariant() left [name=id] blank
      // and the button should already be disabled (FyreATC.update) — bail rather than add a no-SKU variant.
      if (isNaN(variantId)) return;
      var qty = FyreQty.getValue();
      var warrantyId = FyreWarranty.getSelected();
      var properties = {};

      var pdpSection = document.querySelector('[data-pdp-section="' + this.sectionId + '"]') || document;
      // The cart line's own variant is now a real color (getCartVariant swaps Custom Color to its
      // highest-inventory sibling — see FyreProductForm.getCartVariant), so "Custom Color" no longer shows up
      // anywhere on the line by itself. Surface it as its own property, ABOVE the Logo/Caliper Color panel
      // properties harvested below (insertion order = display order in the cart), so it reads as a heading for
      // them rather than the customer/warehouse only seeing "Red" and two color picks with no context.
      var customPanel = pdpSection.querySelector('[data-custom-color-panel]');
      if (customPanel && customPanel.classList.contains('is-visible')) {
        properties['Finish'] = 'Custom Color';
      }
      pdpSection.querySelectorAll('[name^="properties["]').forEach(function (el) {
        if (el.disabled) return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        var key = el.name.slice(11, -1).replace(/^Custom\s+/i, '');
        if (el.value !== '') properties[key] = el.value;
      });

      var vehicle = null;
      try {
        vehicle = window.FitmentStorage ? window.FitmentStorage.get()
          : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null');
      } catch (e) { /* empty */ }
      if (vehicle) {
        properties['Fits'] = vehicle.display ||
          [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].filter(Boolean).join(' ');
      }

      // The real, fulfillable variant actually being added (Custom Color swaps to its highest-inventory
      // sibling — see FyreProductForm.getCartVariant) rather than the customer's on-screen selection,
      // which may be the no-SKU Custom variant itself.
      var currentVariant = FyreProductForm.getSelectedVariant();
      var addedVariant = FyreProductForm.getCartVariant ? FyreProductForm.getCartVariant(currentVariant) : currentVariant;
      if (addedVariant && addedVariant.sku) {
        properties['_sku'] = addedVariant.sku;
      }

      var pad = FyrePadPicker.getSelected();
      var padDefault = FyrePadPicker.getDefault();
      // Model F builder: the kit price already includes ONE pad set (the NS kit BOM), named by the backing's
      // specs.default_pad_set. Keeping the default rides as a PROPERTY on the kit line and costs nothing extra.
      // Choosing a different compound is a SWAP, emitted below as two extra lines. Non-builder products have no
      // kit BOM, so their pad stays an ordinary paid line.
      var padIncludedInKit = !!document.querySelector('[data-fyre-resolve]');
      var padIsSwap = false;
      if (pad && padIncludedInKit) {
        // store the readable compound ("Brembo FM1000"), not the full product title; SKU rides hidden for traceability
        properties['Brake Pad'] = String(pad.name).split('| ').pop().split(' Compound')[0].trim();
        if (pad.sku) properties['_pad_sku'] = pad.sku;
        // The pad the kit is PRICED WITH. NetSuite credits this one back when the buyer swaps, and it cannot
        // re-derive it from the order: by then the kit line only records which pad the buyer chose.
        if (padDefault && padDefault.sku) properties[PADSWAP_DEFAULT_KEY] = padDefault.sku;
        // Without a known default we cannot tell a swap from the pad the kit already includes, and adding a
        // second pad line on that guess would double-charge. Treat it as "no swap" — the kit ships as built.
        padIsSwap = padDefault != null && !FyrePadPicker.isDefaultSelected();
      }

      var mainItem = { id: variantId, quantity: qty };
      if (Object.keys(properties).length) mainItem.properties = properties;

      var items = [mainItem];

      // Keyed on the TOGGLE first, then on the harvested panel properties (which a legacy product selects
      // Custom through as an option value, with no toggle in the markup). Properties alone are not enough:
      // a section whose theme-editor blocks carry no logo/colour inputs harvests nothing, and the kit line
      // has ALREADY been swapped to a colour sibling by the resolver — so a property-only test would ship
      // that sibling at the base price with no paint line and no error.
      var isCustomColor = !!FyreProductForm.customPaintActive
        || properties['Caliper Color'] !== undefined
        || properties['Paint Code'] !== undefined
        || properties['Logo Color'] !== undefined;
      var caliperMatch = FyreProductForm.getMatchedCaliperPaintVariant
        ? FyreProductForm.getMatchedCaliperPaintVariant() : null;
      // Keyed on the toggle itself, not on the harvested panel properties: a section with no logo/colour blocks
      // harvests nothing, and the guard must still hold.
      if (customPaintUnmatched(FyreProductForm.customPaintActive, caliperMatch)) {
        this.showError('Custom paint is not available for this caliper. Untick Custom paint or choose a colour.');
        return;
      }
      if (isCustomColor) {
        if (caliperMatch) {
          var caliperProps = {};
          ['Logo Color', 'Caliper Color', 'Paint Code'].forEach(function (k) {
            if (properties[k] !== undefined) caliperProps[k] = properties[k];
          });
          // Pair by a one-off token generated per submit, not SKU or variant ID — Custom-color variants
          // commonly ship with a blank SKU (confirmed on this store's data), and variant ID alone would collide
          // if the same variant is added twice with two different color configs (two parent lines, same variant
          // ID, ambiguous which addon belongs to which). Both items get the SAME token; only this addon item
          // gets flagged in the cart templates as "hide my own controls" (matched against the product handle).
          var pairId = 'pc' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
          properties['_pair_id'] = pairId;
          caliperProps['_pair_id'] = pairId;
          items.push({ id: caliperMatch.id, quantity: qty, properties: caliperProps });
        } else if (FyreProductForm.caliperPaintData && FyreProductForm.caliperPaintData.pistonMatch) {
          console.warn('[fyre-pdp] custom color selected but no matching Custom Caliper Paint variant found');
        }
      }

      if (pad && pad.variantId && !padIncludedInKit) {
        items.push({ id: pad.variantId, quantity: qty });
      } else if (padIsSwap && pad && pad.variantId) {
        // Keeping the pad the kit ships with adds nothing — it is already in the price. Choosing a different
        // compound produces THREE lines that must be read together: the kit, the chosen pad at list, and a $0
        // PADSWAP marker. The marker is what the discount Function matches on to know a swap happened; without
        // it the swap is invisible to the Function and the buyer pays for two pads.
        // Pair by a per-submit token, not by SKU or variant id: the same kit can be added twice with two
        // different compounds, and the lines would otherwise be unattributable to their kits.
        // One token per submit: when the Custom paint branch above already stamped the kit line, the pad lines
        // join that group instead of overwriting it — otherwise the paint add-on line is orphaned from its kit.
        var padPairId = properties[PADSWAP_PAIR_KEY] || ('ps' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8));
        properties[PADSWAP_PAIR_KEY] = padPairId;
        properties[PADSWAP_CHOSEN_KEY] = String(pad.variantId);
        // Jacob's shape: the chosen-pad line carries the pair token and the addon flag, nothing more. The
        // amounts stay off every line — the credit is the default pad's price AT THE BUYER'S CATALOG, which
        // the theme cannot resolve and the Function looks up.
        var chosenPadProps = {};
        chosenPadProps[PADSWAP_PAIR_KEY] = padPairId;
        chosenPadProps[PADSWAP_ADDON_KEY] = 'true';
        items.push({ id: pad.variantId, quantity: qty, properties: chosenPadProps });
        var markerId = padSwapVariantId();
        if (markerId) {
          var swapProps = {};
          swapProps[PADSWAP_PAIR_KEY] = padPairId;
          items.push({ id: markerId, quantity: qty, properties: swapProps });
        } else {
          // Fail loud rather than silently shipping a swap the Function cannot see.
          console.error('[fyre-pdp] pad swap chosen but the PADSWAP marker variant is absent — the discount '
            + 'Function will not see this swap. Check that the pad-swap product is published and that the '
            + 'brake_pad_picker block rendered #pdp-padswap-variant.');
        }
      }
      if (warrantyId) items.push({ id: parseInt(warrantyId), quantity: 1 });

      this.setLoading(true);
      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || 'Could not add to cart'); });
          return res.json();
        })
        .then(function (data) {
          self.setLoading(false);
          document.dispatchEvent(new CustomEvent('atc:added', {
            detail: { product_id: data.items ? data.items[0].product_id : null }
          }));
        })
        .catch(function (err) {
          self.setLoading(false);
          self.showError(err.message || 'Could not add to cart. Please try again.');
        });
    },

    // A failed /cart/add.js call (commonly a sold-out line) surfaces here — a small message BELOW the button —
    // rather than in the button's own (fixed-height, all-caps) text, where a full backend error sentence blows
    // the button out to several lines.
    showError: function (msg) {
      if (!this.errorEl) return;
      clearTimeout(this.errorTimer);
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      var self = this;
      this.errorTimer = setTimeout(function () { self.errorEl.hidden = true; }, 6000);
    },

    setLoading: function (loading) {
      if (!this.btn) return;
      this.btn.disabled = loading;
      var textEl = this.btn.querySelector('[data-atc-text]');
      if (textEl) textEl.textContent = loading ? 'ADDING...' : 'ADD TO CART';
    }
  };

  /* ── Pair With Carousel ── */
  var FyrePairWith = {
    init: function () {
      var carousel = document.querySelector('[data-pair-carousel]');
      if (!carousel) return;
      var productId = carousel.dataset.pairProductId;
      var intent = carousel.dataset.pairIntent || 'complementary';
      var limit = carousel.dataset.pairLimit || 12;
      var emptyState = carousel.dataset.pairEmptyState || 'placeholder';
      var emptyMessage = carousel.dataset.pairEmptyMessage || 'No complementary products available.';
      var track = carousel.querySelector('[data-pair-track]');
      var navBar = carousel.querySelector('[data-pair-nav-bar]');
      var nav = carousel.querySelector('.pdp-pair-with__nav');
      var prev = carousel.querySelector('[data-pair-prev]');
      var next = carousel.querySelector('[data-pair-next]');
      if (!track || !productId) return;

      var dragging = false, dragStartX = 0, dragStartLeft = 0;

      function updateNav() {
        if (!navBar) return;
        var maxScroll = track.scrollWidth - track.offsetWidth;
        if (maxScroll <= 0) { navBar.style.width = '100%'; navBar.style.left = '0%'; return; }
        var bw = track.offsetWidth / track.scrollWidth * 100;
        var left = track.scrollLeft / maxScroll * (100 - bw);
        navBar.style.width = bw + '%';
        navBar.style.left = Math.max(0, Math.min(100 - bw, left)) + '%';
      }

      track.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', updateNav);

      if (nav) {
        nav.addEventListener('click', function (e) {
          if (dragging) return;
          var rect = nav.getBoundingClientRect();
          var bw = track.offsetWidth / track.scrollWidth * rect.width;
          var range = rect.width - bw;
          if (range <= 0) return;
          var ratio = Math.max(0, Math.min(1, (e.clientX - rect.left - bw / 2) / range));
          track.scrollTo({ left: ratio * (track.scrollWidth - track.offsetWidth), behavior: 'smooth' });
        });
      }

      function startDrag(clientX) { dragging = true; dragStartX = clientX; dragStartLeft = parseFloat(navBar.style.left) || 0; navBar.classList.add('pdp-pair-with__nav-bar--dragging'); }
      function onMove(clientX) {
        if (!dragging || !nav) return;
        var rect = nav.getBoundingClientRect();
        var bw = track.offsetWidth / track.scrollWidth * 100;
        var range = 100 - bw;
        if (range <= 0) return;
        var dx = (clientX - dragStartX) / rect.width * 100;
        track.scrollLeft = Math.max(0, Math.min(range, dragStartLeft + dx)) / range * (track.scrollWidth - track.offsetWidth);
      }
      function endDrag() { if (!dragging) return; dragging = false; navBar.classList.remove('pdp-pair-with__nav-bar--dragging'); }

      if (navBar) {
        navBar.addEventListener('mousedown', function (e) { e.preventDefault(); e.stopPropagation(); startDrag(e.clientX); });
        navBar.addEventListener('touchstart', function (e) { e.stopPropagation(); startDrag(e.touches[0].clientX); }, { passive: true });
      }
      document.addEventListener('mousemove', function (e) { onMove(e.clientX); });
      document.addEventListener('touchmove', function (e) { onMove(e.touches[0].clientX); }, { passive: true });
      document.addEventListener('mouseup', endDrag);
      document.addEventListener('touchend', endDrag);
      requestAnimationFrame(updateNav);

      function bindAtcBtn(btn) {
        btn.addEventListener('click', function () {
          var vid = parseInt(btn.dataset.pairAtc);
          if (!vid) return;
          btn.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
          })
            .then(function (r) { return r.json(); })
            .then(function () {
              btn.disabled = false;
              btn.textContent = 'ADDED ✓';
              setTimeout(function () { btn.textContent = 'ADD TO CART +'; }, 2000);
              document.dispatchEvent(new CustomEvent('atc:added', { detail: {} }));
            })
            .catch(function () { btn.disabled = false; });
        });
      }

      fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + limit + '&intent=' + intent)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = data.products || [];
          if (!products.length) {
            if (emptyState === 'hide') { carousel.style.display = 'none'; }
            else {
              var viewport = carousel.querySelector('.pdp-pair-with__viewport');
              if (viewport) {
                var msg = document.createElement('div');
                msg.className = 'pdp-pair-with__no-results';
                msg.textContent = emptyMessage;
                viewport.appendChild(msg);
              }
              carousel.classList.add('pdp-pair-with--empty');
            }
            return;
          }
          track.innerHTML = products.map(function (p) {
            var price = p.compare_at_price_min && p.compare_at_price_min > p.price_min
              ? '<s class="pdp-pair-with__price-compare">' + formatMoney(p.compare_at_price_min) + '</s> ' + formatMoney(p.price_min)
              : formatMoney(p.price_min);
            var imgSrc = p.featured_image ? p.featured_image.split('?')[0] + '?width=150&format=webp' : '';
            var img = imgSrc
              ? '<img src="' + imgSrc + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" class="pdp-pair-with__product-img" loading="lazy">'
              : '<div class="pdp-pair-with__product-img pdp-pair-with__product-img--placeholder"></div>';
            var vid = p.variants && p.variants[0] ? p.variants[0].id : '';
            var available = p.available;
            return '<div class="pdp-pair-with__product">' +
              '<a href="' + p.url + '" class="pdp-pair-with__product-img-wrap">' + img + '</a>' +
              '<p class="pdp-pair-with__product-name">' + (p.title || '') + '</p>' +
              '<p class="pdp-pair-with__product-price">' + price + '</p>' +
              (available
                ? '<button class="pdp-pair-with__product-atc" data-pair-atc="' + vid + '" type="button">ADD TO CART +</button>'
                : '<span class="pdp-pair-with__product-sold-out">Sold out</span>') +
              '</div>';
          }).join('');
          track.querySelectorAll('[data-pair-atc]').forEach(bindAtcBtn);
          requestAnimationFrame(updateNav);
        })
        .catch(function () { carousel.style.display = 'none'; });

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -140, behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: 140, behavior: 'smooth' }); });
    }
  };

  /* ── Toggles ── */
  var FyreToggles = {
    openToggle: function (toggle) {
      var content = toggle.querySelector('[data-pdp-toggle-content]');
      var btn = toggle.querySelector('[data-pdp-toggle-btn]');
      if (!content) return;
      toggle.classList.add('pdp-toggle--open');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      content.style.maxHeight = content.scrollHeight + 'px';
      function onEnd(e) {
        if (e.propertyName !== 'max-height') return;
        content.removeEventListener('transitionend', onEnd);
        if (toggle.classList.contains('pdp-toggle--open')) content.style.maxHeight = 'none';
      }
      content.addEventListener('transitionend', onEnd);
      toggle.dispatchEvent(new CustomEvent('toggle:opened', { bubbles: true }));
    },
    closeToggle: function (toggle) {
      var content = toggle.querySelector('[data-pdp-toggle-content]');
      var btn = toggle.querySelector('[data-pdp-toggle-btn]');
      if (!content) return;
      toggle.classList.remove('pdp-toggle--open');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      content.style.maxHeight = content.scrollHeight + 'px';
      content.offsetHeight; // force reflow
      content.style.maxHeight = '0';
    },
    init: function () {
      var self = this;
      document.querySelectorAll('[data-pdp-toggle]').forEach(function (toggle) {
        var btn = toggle.querySelector('[data-pdp-toggle-btn]');
        var content = toggle.querySelector('[data-pdp-toggle-content]');
        if (!btn || !content) return;
        if (toggle.classList.contains('pdp-toggle--open')) content.style.maxHeight = 'none';
        btn.addEventListener('click', function () {
          if (toggle.classList.contains('pdp-toggle--open')) self.closeToggle(toggle);
          else self.openToggle(toggle);
        });
      });
    }
  };

  /* ── FPS Tabs ── */
  var FyreFpsTabs = {
    init: function () {
      document.querySelectorAll('[data-fps-toggle]').forEach(function (container) {
        var btns = container.querySelectorAll('[data-fps-tab]');
        var panels = container.querySelectorAll('[data-fps-panel]');
        var slider = container.querySelector('.fps-tabs__slider');
        if (!btns.length) return;

        function moveSlider(btn) {
          if (!slider) return;
          slider.style.left = btn.offsetLeft + 'px';
          slider.style.width = btn.offsetWidth + 'px';
        }

        btns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            var target = btn.dataset.fpsTab;
            btns.forEach(function (b) {
              b.classList.toggle('fps-tabs__btn--active', b === btn);
              b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
            });
            panels.forEach(function (p) {
              p.classList.toggle('pdp-fps-toggle__panel--active', p.dataset.fpsPanel === target);
            });
            moveSlider(btn);
          });
        });

        function positionSlider() {
          var active = container.querySelector('.fps-tabs__btn--active');
          if (active && slider) {
            slider.style.transition = 'none';
            moveSlider(active);
            slider.offsetWidth; // force reflow
            slider.style.transition = '';
          }
        }

        if (document.fonts && document.fonts.ready) document.fonts.ready.then(positionSlider);
        else requestAnimationFrame(positionSlider);

        document.addEventListener('toggle:opened', function () { requestAnimationFrame(positionSlider); });
        window.addEventListener('resize', positionSlider);
      });
    }
  };

  /* ── Description Toggle ── */
  var FyreDescToggle = {
    init: function () {
      var desc = document.querySelector('[data-pdp-desc]');
      var btn = document.querySelector('[data-pdp-desc-btn]');
      if (!desc || !btn) return;
      if (desc.scrollHeight <= desc.clientHeight) { btn.style.display = 'none'; return; }
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          desc.classList.remove('pdp-desc-content--expanded');
          btn.setAttribute('aria-expanded', 'false');
          btn.querySelector('[data-pdp-desc-btn-label]').textContent = 'Read more';
        } else {
          desc.classList.add('pdp-desc-content--expanded');
          btn.setAttribute('aria-expanded', 'true');
          btn.querySelector('[data-pdp-desc-btn-label]').textContent = 'Read less';
        }
      });
    }
  };

  /* ── Fitment Selector ── */
  // Submodel BASE sentinel. This file is the ONLY engine that implements it: the four
  // inline cascades in the vehicle-selector section, the element snippet and the finder
  // header do not, so a base-vs-trim pick is disambiguated on the PDP and nowhere else.
  // A blank-submodel BASE vehicle that coexists with named
  // trims (e.g. 2018 Corvette '' alongside Z06/Grand Sport) is surfaced as an explicit
  // "Base / Standard" option; the sentinel maps back to submodel '' at match time.
  // The value must never collide with a real trim name ('Base' EXISTS in the data —
  // hence the distinct label) nor with the '' placeholder.
  var BASE_SUBMODEL_VALUE = '__base__';
  var BASE_SUBMODEL_LABEL = 'Base / Standard';

  var FyreFitment = {
    block: null, vehicles: [], sel: {},

    init: function () {
      this.block = document.querySelector('[data-pdp-fitment]');
      if (!this.block) return;
      var sid = this.block.dataset.fitmentSection;
      var dataEl = document.getElementById('pdp-fitment-data-' + sid);
      if (!dataEl) return;
      this.vehicles = [];

      // Per-product fitment vehicle handles → O(1) fit lookup. Empty/missing = cannot verify.
      this.fitHandles = {};
      this.fitCount = 0;
      var fitEl = document.getElementById('pdp-fitment-product-' + sid);
      if (fitEl) {
        try {
          var handles = JSON.parse(fitEl.textContent || '[]');
          var fh = this.fitHandles;
          handles.forEach(function (h) { if (h) { fh[h] = true; } });
          this.fitCount = Object.keys(fh).length;
        } catch (e) { /* empty */ }
      }

      // OWN-group-only handles → the fit verdict derives from the product's own
      // fitment.groups, never collection-appended vehicles. Empty/missing = fall back to fitHandles.
      this.ownFitHandles = {};
      this.ownFitCount = 0;
      var ownEl = document.getElementById('pdp-fitment-own-' + sid);
      if (ownEl) {
        try {
          var ownHandles = JSON.parse(ownEl.textContent || '[]');
          var ofh = this.ownFitHandles;
          ownHandles.forEach(function (h) { if (h) { ofh[h] = true; } });
          this.ownFitCount = Object.keys(ofh).length;
        } catch (e) { /* empty */ }
      }

      var storefrontUrl  = dataEl.dataset.storefrontUrl;
      var storefrontToken = dataEl.dataset.storefrontToken;
      this._storefrontUrl   = storefrontUrl;
      this._storefrontToken = storefrontToken;
      var self = this;

      // postLoad() reads this.sel and this.inferredType, and the no-catalogue path below
      // calls it SYNCHRONOUSLY — so the selector wiring is assigned FIRST. With it after
      // the load branch, that path threw out of init() inside DOMContentLoaded and took
      // every later initialiser on the page down with it.
      var b = this.block;
      this.sel = {
        type: b.querySelector('[data-fitment-level="type"]'),
        make: b.querySelector('[data-fitment-level="make"]'),
        model: b.querySelector('[data-fitment-level="model"]'),
        submodel: b.querySelector('[data-fitment-level="submodel"]'),
        year: b.querySelector('[data-fitment-level="year"]')
      };

      ['type', 'make', 'model', 'submodel', 'year'].forEach(function (level) {
        self.sel[level].addEventListener('change', function () {
          self.update(level);
        });
      });

      // A `change` hook cannot reach the retry AT ALL, which is why there is no retryCatalogue()
      // call above: with an empty catalogue populate() disables every select and a disabled select
      // never fires change, and with a non-empty one retryCatalogue() returns on its own
      // vehicles.length guard. Both halves are dead, so the call was removed rather than left as a
      // comment claiming a trigger that cannot fire.
      //
      // The reachable triggers are on the BLOCK. `click` is the primary one: it is a completed tap
      // rather than the start of a scroll gesture, so a mobile shopper swiping over the block does
      // not spend the one retry. It reaches the block because `.pdp-fitment__select:disabled` sets
      // pointer-events:none (fyre-template-product.css), making the wrapper the hit target — a
      // disabled control swallows click in every engine, so the CSS is what makes this trigger
      // real. pointerdown and focusin are kept as belt-and-braces; the `_retried` latch means the
      // first one to arrive is the only one that costs anything.
      ['click', 'pointerdown', 'focusin'].forEach(function (evt) {
        b.addEventListener(evt, function () { self.retryCatalogue(); });
      });

      // The vehicle dropdown is populated from the product's OWN fitment (see postLoad); it must
      // NOT depend on collection membership. Load only the vehicle universe here — the per-product
      // filter happens in postLoad. (FitmentCollections is still loaded lazily by updateShopBtn.)
      //
      // A product is Auto or Moto, never both, and the template already says which, so only that
      // catalogue is fetched. This is the one selector that loads eagerly on every page view, so
      // it is where the split saves the most.
      //
      // Load it through the theme-wide module (assets/fyre-fitment.js), which memoises per
      // resolved URL and is shared with every <fyre-vehicle-selector> on the page — so the
      // PDP and the header selector make ONE request, not two. There is deliberately no
      // raw-fetch fallback to the retired combined catalogue: it is 4.2 MB against 485 KB
      // for the Auto split file and 1.1 MB for Moto, and a fallback that large fires
      // whenever script order slips.
      //
      // The type is NORMALISED rather than trusted: an unrecognised value is a data defect,
      // and defaulting it to Auto would serve a motorcycle shopper the wrong catalogue in
      // silence. Anything but auto/moto warns and loads nothing.
      this._dataEl = dataEl;
      var rawType = b.dataset.fitmentType || '';
      var pdpType = /^moto$/i.test(rawType) ? 'Moto' : (/^auto$/i.test(rawType) ? 'Auto' : '');
      if (!pdpType && window.console && console.warn) {
        console.warn('[fyre-pdp] unrecognised data-fitment-type ' + JSON.stringify(rawType) +
          ' — expected "Auto" or "Moto"; vehicle catalogue not loaded');
      }
      this._pdpType = pdpType;
      // The forced Type for the cascade. An unrecognised value keeps its raw form rather
      // than becoming '' — a blank inferredType would un-hide the Type dropdown on a
      // product whose template plainly declares one.
      this.inferredType = pdpType || rawType;
      this._retried = false;
      this._requested = false;
      this._warnedNoCatalogue = false;
      this._noModule = false;
      this._src = '';
      this._loading = null;
      this.loadCatalogue(true);

      var removeBtn = b.querySelector('[data-fitment-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', function () {
          if (window.FitmentStorage) window.FitmentStorage.remove();
          else { try { localStorage.removeItem('fitment_vehicle'); } catch (e) { /* empty */ } }
          self.applyVehicle(null);
          window.dispatchEvent(new CustomEvent('fitment:changed', { detail: null }));
        });
      }

      window.addEventListener('fitment:changed', function (e) {
        self.retryCatalogue();
        self.applyVehicle(e.detail);
        self.updateShopBtn(e.detail, storefrontUrl, storefrontToken);
      });
    },

    /*
     * Resolve the vehicle catalogue for this product's type.
     *
     * `initial` runs postLoad() unconditionally — an empty catalogue still has to render the
     * selector and reach `pdp-fitment--ready`. A retry re-runs postLoad only when the row
     * count actually changed, so a second empty answer re-renders nothing and does not fire
     * a duplicate fitment:verified.
     *
     * `_loading` is the in-flight promise. It is what makes the retry honest: without it a
     * trigger arriving DURING the initial load spends the one retry on the same memoised
     * promise, which is not a re-request at all.
     */
    loadCatalogue: function (initial) {
      var self = this;
      var before = (this.vehicles && this.vehicles.length) || 0;
      function done(rows) {
        self._loading = null;
        self.vehicles = rows || [];
        // BOTH attempts landed empty: the initial load and the one-shot retry. Every select is
        // now disabled and ATC reads "SELECT VEHICLE FIRST" with no way to satisfy it — and
        // before this line nothing reached the console, because loadUrl()'s catch returns []
        // silently and _catalogue() only warns on a SYNCHRONOUS throw. Name the asset that WAS
        // requested so the reader can check the CDN directly.
        //
        // Gated on `_requested`, which SUBSUMES the type check — the flag is only ever set after
        // _catalogue() has accepted the type — so a separate `_pdpType` clause here would be dead
        // by construction. An unrecognised data-fitment-type, an absent
        // catalogue module, or an unset src all resolve [] without issuing a single fetch, and
        // a line claiming "empty after initial load AND retry … reload to try again" would send
        // someone debugging a bricked purchase path to the CDN for a template data defect that
        // no reload can fix. Those cases already have their own warn.
        if (!initial && !self.vehicles.length && self._requested) self.warnDoubleFailure();
        if (initial || self.vehicles.length !== before) self.postLoad();
      }
      this._loading = this._catalogue(this._pdpType)
        .then(done)
        .catch(function () { done([]); });
      return this._loading;
    },

    // The asset THIS page actually requested, RECORDED by _catalogue() from the loader module's
    // own resolver — never re-derived here. A second implementation of the resolution rule is a
    // second thing to drift: FitmentData.srcFor falls back to the combined `data-vehicle-src`
    // attribute and FyreFitment.srcFor honours configure() overrides, neither of which a local
    // dataset read can see. When they disagreed, `_requested` said "nothing was asked for" on a
    // page that had just issued a fetch — killing the one-shot retry — and the double-failure
    // warn printed `src=unset` for a file that was genuinely requested.
    catalogueSrc: function () {
      return this._src || '';
    },

    // One console line plus the QA attribute, at most once per page, for a catalogue failure that
    // happens BEFORE any request goes out. Silence here was the C2 defect: the block reached
    // `pdp-fitment--ready` with every select dead and nothing anywhere said why, so the two
    // cheapest-to-fix failures on this path (no module on the page, unresolved src) were the only
    // ones with no observable at all. The message is distinct per cause because the fixes are
    // different files — a script tag versus a template attribute.
    flagCatalogueError: function (msg) {
      if (this._warnedNoCatalogue) return;
      this._warnedNoCatalogue = true;
      try {
        if (this.block) this.block.dataset.catalogueError = '1';
      } catch (e) { /* never let diagnostics break the PDP */ }
      try {
        if (window.console && console.warn) console.warn(msg);
      } catch (e) { /* never let diagnostics break the PDP */ }
    },

    // One console line for a purchase path that is now bricked — never a throw, and never more
    // than once per page view (the retry is one-shot, so this can only be reached once).
    //
    // Also raises `data-catalogue-error` on the block. The notes loader already raises
    // `data-notes-error` for the LESS severe failure (guidance lost); leaving the failure that
    // makes the product unbuyable console-only meant the more expensive one was the less
    // observable one, and nobody reads a shopper's console. QA and synthetic monitoring assert
    // on the attribute.
    warnDoubleFailure: function () {
      try {
        if (this.block) this.block.dataset.catalogueError = '1';
      } catch (e) { /* never let diagnostics break the PDP */ }
      try {
        if (!(window.console && console.warn)) return;
        console.warn('[fyre-pdp] vehicle catalogue empty after initial load AND retry' +
          ' (type=' + this._pdpType +
          ', src=' + (this.catalogueSrc() || 'unset') +
          ') — the vehicle selector will stay disabled; reload to try again');
      } catch (e) { /* never let diagnostics break the PDP */ }
    },

    /*
     * Ask the loader module for the src it WOULD use, without reimplementing its rule.
     * Returns '' when the module cannot answer, which is itself a reportable failure.
     */
    _srcFromModule: function (mod, type, args) {
      try {
        if (mod && typeof mod.srcFor === 'function') return mod.srcFor.apply(mod, args) || '';
      } catch (e) { /* a resolver that throws resolves to "unknown", never to a guess */ }
      return '';
    },

    // A synchronous throw from either module must not escape init(); an unknown type resolves
    // to [] rather than guessing a catalogue.
    _catalogue: function (type) {
      if (!type) return Promise.resolve([]);
      // `_requested` is the honest record of whether a fetch could have gone out: a module has to
      // be present AND the src IT resolves has to be non-empty. Without it the retry latch and the
      // double-failure warn both fire on pages that never asked the network anything.
      //
      // The src comes from the module's OWN resolver, and the flag is set only AFTER the loader
      // call has returned. A loadVehicles() that throws synchronously reached no network, so a
      // `_requested` set before the call would license a retry and a "both attempts failed" warn
      // for a page on which nothing was ever attempted.
      var src = '';
      this._noModule = false;
      try {
        if (window.FyreFitment && typeof window.FyreFitment.loadVehicles === 'function') {
          src = this._srcFromModule(window.FyreFitment, type, [type]);
          var p = Promise.resolve(window.FyreFitment.loadVehicles(type));
          this._src = src;
          if (src) this._requested = true;
          else this.flagCatalogueError('[fyre-pdp] vehicle catalogue src did not resolve for type=' +
            type + ' — check data-vehicle-src-auto/-moto on the fitment data element' +
            '; the vehicle selector will stay disabled');
          return p;
        }
        if (window.FitmentData && this._dataEl) {
          src = this._srcFromModule(window.FitmentData, type, [this._dataEl, type]);
          var p2 = Promise.resolve(window.FitmentData.loadType(this._dataEl, type));
          this._src = src;
          if (src) this._requested = true;
          else this.flagCatalogueError('[fyre-pdp] vehicle catalogue src did not resolve for type=' +
            type + ' — check data-vehicle-src-auto/-moto on the fitment data element' +
            '; the vehicle selector will stay disabled');
          return p2;
        }
        // Recorded so retryCatalogue() can tell "no module was on the page" — the one initial
        // failure a later trigger can genuinely do better on — from "the module answered and
        // its src was empty", which resolves the same way however often it is re-asked.
        this._noModule = true;
        this.flagCatalogueError('[fyre-pdp] no vehicle catalogue module on the page' +
          ' (neither window.FyreFitment nor window.FitmentData) — check the asset script tags' +
          '; the vehicle selector will stay disabled');
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[fyre-pdp] vehicle catalogue module threw synchronously: ' +
            (e && e.message ? e.message : e));
        }
      }
      return Promise.resolve([]);
    },

    /*
     * One-shot retry, fired by the first touch of the selector or a vehicle chosen elsewhere.
     * A REAL retry, not a cache read: FyreFitment.loadUrl drops its memo slot both when the
     * request fails AND when it resolves an EMPTY catalogue, so the next call re-requests the
     * asset. (Before that second case existed, a 200 carrying `[]` kept the memo and the "retry"
     * was a pure cache read that issued no request while burning the one attempt.)
     *
     * A trigger while a load is still in flight is a no-op — the answer is already coming, and
     * spending the retry on it would burn the one attempt on the promise already awaited.
     * `_retried` is therefore set only when a fresh request is actually issued — which is also
     * why `_requested` gates it: if the initial load never reached the network (unrecognised
     * type, unset src) a second pass would resolve the same way, so burning the latch and
     * warning about a double failure would both be fiction. The flag is set only after
     * _catalogue() accepts the type, so it also stands in for the type check.
     *
     * The ONE case where a second pass genuinely can do better is an absent module that has
     * since arrived: a blocked or late catalogue script leaves `_requested` false at init, and
     * on `main` every trigger re-entered _catalogue() and so recovered. Re-check for a module
     * before honouring the bail, or the gate turns a recoverable state into a dead selector.
     */
    retryCatalogue: function () {
      if (this._retried || this._loading) return;
      if (!this._requested && !(this._noModule && (window.FyreFitment || window.FitmentData))) return;
      if (this.vehicles && this.vehicles.length) return;
      this._retried = true;
      this.loadCatalogue(false);
    },

    postLoad: function () {
      var s = this.sel;
      var existing = null;
      if (window.FitmentStorage) existing = window.FitmentStorage.get();
      else { try { existing = JSON.parse(localStorage.getItem('fitment_vehicle')); } catch (e) { /* empty */ } }

      // Global vehicle finder: the dropdown lists EVERY vehicle (the cascade already scopes it to
      // the forced Type — Auto/Moto — via inferredType). Fitment lives on the product and drives
      // the VERDICT (applyVehicle reads ownFitHandles/fitHandles), NOT the dropdown — so a customer
      // can check ANY vehicle and get a fits/doesn't-fit answer, then use the "shop your vehicle"
      // link on a no-fit. Collection membership plays no part: a product-scoped list would make the
      // verdict trivial and hide the customer's own vehicle exactly when it doesn't fit.
      this.fitVehicles = this.vehicles;

      if (this.inferredType) {
        s.type.style.display = 'none';
        this.populate(s.type, this.unique(this.fitVehicles.map(function (v) { return v.type; })), 'Vehicle Type');
        s.type.value = this.inferredType;
      }

      if (existing) {
        this.applyVehicle(existing);
        this.updateShopBtn(existing, this._storefrontUrl, this._storefrontToken);
      } else {
        this.update('type');
        this.updateShopBtn(null);
      }

      this.updateVisibility();
      this.block.classList.add('pdp-fitment--ready');
    },

    unique: function (arr) { return Array.from(new Set(arr)).filter(Boolean).sort(); },

    // Cascade order is Type → Year → Make → Model → Submodel (classic YMM).
    // filtered(level) returns vehicles matching every selection ABOVE `level`.
    // Submodel is terminal (no level depends on it) so it never filters here.
    filtered: function (level) {
      var list = this.fitVehicles || this.vehicles;
      var s = this.sel;
      if (level !== 'type' && s.type.value) list = list.filter(function (v) { return v.type === s.type.value; });
      if (level !== 'type' && level !== 'year' && s.year.value) list = list.filter(function (v) { return v.year === s.year.value; });
      if (level !== 'type' && level !== 'year' && level !== 'make' && s.make.value) list = list.filter(function (v) { return v.make === s.make.value; });
      if (level !== 'type' && level !== 'year' && level !== 'make' && level !== 'model' && s.model.value) list = list.filter(function (v) { return v.model === s.model.value; });
      return list;
    },

    // Entries are plain strings (option value === label) or { value, label } pairs —
    // the latter only for the submodel Base sentinel, whose stored value differs
    // from its display label.
    populate: function (selectEl, values, placeholder) {
      var current = selectEl.value;
      selectEl.innerHTML = '<option value="">' + placeholder + '</option>';
      values.forEach(function (v) {
        var isPair = typeof v === 'object';
        var val = isPair ? v.value : v;
        var opt = document.createElement('option');
        opt.value = val; opt.textContent = isPair ? v.label : v;
        if (val === current) opt.selected = true;
        selectEl.appendChild(opt);
      });
      selectEl.disabled = values.length === 0;

      // Scope the empty-catalogue affordance to the state it exists for. The CSS that makes the
      // wrapper the hit target (pointer-events:none on the disabled select) is there for ONE
      // failure: no catalogue loaded, so the shopper's tap has to reach the block and spend the
      // one-shot retry. Applied to every disabled select it also swallowed the tap on a select
      // that is disabled for the ordinary reason — Make before Year is chosen — and painted a
      // not-allowed cursor over a perfectly healthy cascade.
      if (this.block && this.block.classList) {
        if (this.vehicles && this.vehicles.length) this.block.classList.remove('pdp-fitment--catalogue-empty');
        else this.block.classList.add('pdp-fitment--catalogue-empty');
      }
    },

    repopulateAll: function () {
      var s = this.sel;
      if (!this.inferredType) {
        this.populate(s.type, this.unique((this.fitVehicles || this.vehicles).map(function (v) { return v.type; })), 'Vehicle Type');
      }
      // Year filtered by Type only (second in the cascade), newest first.
      var years = s.type.value ? this.unique(this.filtered('year').map(function (v) { return v.year; })) : [];
      years.sort(function (a, b) { return parseInt(b) - parseInt(a); });
      this.populate(s.year, years, 'Year');

      this.populate(s.make, s.year.value ? this.unique(this.filtered('make').map(function (v) { return v.make; })) : [], 'Make');
      this.populate(s.model, s.make.value ? this.unique(this.filtered('model').map(function (v) { return v.model; })) : [], 'Model');

      // Submodel is terminal + optional: shown only once a Model is chosen. A model
      // with no submodels leaves it empty/disabled and the vehicle is complete after
      // Model (saveVehicle matches (v.submodel||'') === '').
      if (s.model.value) {
        var subList = this.filtered('submodel');
        var submodels = this.unique(subList.map(function (v) { return v.submodel; })).filter(Boolean);
        if (submodels.length && subList.some(function (v) { return !v.submodel; })) {
          submodels.unshift({ value: BASE_SUBMODEL_VALUE, label: BASE_SUBMODEL_LABEL });
        }
        this.populate(s.submodel, submodels, 'Submodel');
      } else {
        this.populate(s.submodel, [], 'Submodel');
      }
      this.updateVisibility();
    },

    updateVisibility: function () {
      var s = this.sel;
      var hasType = s.type.value || this.inferredType;
      s.type.style.display = this.inferredType ? 'none' : '';
      // Progressive reveal follows the cascade: Type → Year → Make → Model → Submodel.
      s.year.style.display = hasType ? '' : 'none';
      s.make.style.display = s.year.value ? '' : 'none';
      s.model.style.display = s.make.value ? '' : 'none';

      var submodels = s.submodel.options.length > 1;
      s.submodel.style.display = (s.model.value && submodels) ? '' : 'none';
    },

    update: function (changedLevel) {
      var levels = ['type', 'year', 'make', 'model', 'submodel'];
      var idx = levels.indexOf(changedLevel);
      for (var i = idx + 1; i < levels.length; i++) this.sel[levels[i]].value = '';
      this.repopulateAll();
      // Save fires only on a COMPLETE selection — saveVehicle no-ops without a full
      // match, so this stays silent on Year alone (Year is now second in the cascade).
      // A no-submodel vehicle is complete after Model; one with submodels after Submodel.
      this.saveVehicle();
    },

    saveVehicle: function () {
      var s = this.sel;
      // Completion invariant — enforced here only; see the BASE sentinel note above. When the
      // submodel select holds real options (>1 = placeholder + trims, plus the Base
      // sentinel when a blank-submodel base vehicle also exists), the bare placeholder
      // ('' = nothing chosen) is NOT committable. Without this, picking the Model
      // auto-commits the base entry and applyVehicle collapses the selector to the
      // saved-vehicle view — dead-ending the shopper with the fitting trim unreachable
      // inside the hidden .pdp-fitment__selects. The base cohort commits through the
      // explicit "Base / Standard" option instead (base and trim fitment can diverge,
      // so forcing either onto the other gives a wrong verdict).
      if (s.submodel.options.length > 1 && !s.submodel.value) return;
      // Sentinel → data value: "Base / Standard" matches the blank-submodel row.
      var subVal = s.submodel.value === BASE_SUBMODEL_VALUE ? '' : s.submodel.value;
      var match = this.vehicles.find(function (v) {
        return v.type === s.type.value && v.make === s.make.value && v.model === s.model.value &&
          (v.submodel || '') === subVal && v.year === s.year.value;
      });
      if (!match) return;
      if (window.FitmentStorage) window.FitmentStorage.set(match);
      else { try { localStorage.setItem('fitment_vehicle', JSON.stringify(match)); } catch (e) { /* empty */ } }
      window.dispatchEvent(new CustomEvent('fitment:changed', { detail: match }));
    },

    applyVehicle: function (vehicle) {
      var b = this.block;
      var s = this.sel;
      var nameEl = b.querySelector('[data-fitment-vehicle-name]');

      if (!vehicle) {
        s.make.value = ''; s.model.value = ''; s.submodel.value = ''; s.year.value = '';
        if (this.inferredType) {
          s.type.value = this.inferredType;
        } else {
          s.type.value = '';
        }
        this.repopulateAll();
        b.classList.remove('pdp-fitment--has-vehicle', 'pdp-fitment--confirmed', 'pdp-fitment--no-fit');
        if (nameEl) nameEl.textContent = '';
        return;
      }

      // Populate each level then set value (Type → Year → Make → Model → Submodel) —
      // setting before options exist is silently ignored by the browser.
      // Match postLoad/repopulateAll — scope the Type list to fitVehicles (this.vehicles is the full
      // vehicle universe), so applyVehicle can't surface unfitting types.
      this.populate(s.type, this.unique((this.fitVehicles || this.vehicles).map(function (v) { return v.type; })), 'Vehicle Type');
      s.type.value = vehicle.type || '';
      var years = s.type.value ? this.unique(this.filtered('year').map(function (v) { return v.year; })) : [];
      if (years.length) years.sort(function (a, b) { return parseInt(b) - parseInt(a); });
      this.populate(s.year, years, 'Year');
      s.year.value = vehicle.year || '';
      var makes = s.year.value ? this.unique(this.filtered('make').map(function (v) { return v.make; })) : [];
      this.populate(s.make, makes, 'Make');
      s.make.value = vehicle.make || '';
      var models = s.make.value ? this.unique(this.filtered('model').map(function (v) { return v.model; })) : [];
      this.populate(s.model, models, 'Model');
      s.model.value = vehicle.model || '';
      var subList = s.model.value ? this.filtered('submodel') : [];
      var submodels = this.unique(subList.map(function (v) { return v.submodel; })).filter(Boolean);
      if (submodels.length && subList.some(function (v) { return !v.submodel; })) {
        submodels.unshift({ value: BASE_SUBMODEL_VALUE, label: BASE_SUBMODEL_LABEL });
      }
      this.populate(s.submodel, submodels, 'Submodel');
      // A saved BASE vehicle stores submodel '' — re-select its sentinel option when
      // trims exist (the sentinel is prepended whenever the base row is present);
      // plain '' otherwise (no-submodel model: empty, disabled select).
      s.submodel.value = vehicle.submodel || (submodels.length ? BASE_SUBMODEL_VALUE : '');

      b.classList.add('pdp-fitment--has-vehicle');
      // BUILDER UMBRELLAS: the resolver (fyre-resolve.js driveFitmentBar) owns the verdict from the backing graph.
      // The umbrella shell carries no fitment (fitCount === 0), so defer entirely to it.
      if (!document.querySelector('[data-fyre-resolve]')) {
        b.classList.remove('pdp-fitment--confirmed', 'pdp-fitment--no-fit');
        // Verdict from the product's OWN fitment groups, synchronously. A collection-derived
        // lookup returns null for any own-group vehicle whose group has no fitment-suffixed
        // collection, which reads as a false "does not fit". ownFitHandles is the product's own
        // vehicles only; fitHandles (which includes collection-appended vehicles) is a fallback
        // ONLY when no own-group data was emitted.
        // Vehicle-grain fitment (moto/PS, product-level fitment.vehicles): the whole verdict is owned
        // by fyre-pdp-vehicle-compat.js (paged Storefront fetch — Liquid can't emit the >50-vehicle
        // handle list). Skip EVERY server-handle branch below — even a collection-appended fitHandles
        // verdict would duel with the async one — and stay fitPending (disabled) until it dispatches
        // fitment:verified / fitment:verify-unavailable. Fail-closed while in flight.
        if (b.getAttribute('data-has-vehicle-fitment') !== 'true') {
          var fits;
          if (this.ownFitCount > 0) {
            fits = this.ownFitHandles[vehicle.id] === true;
            b.classList.add(fits ? 'pdp-fitment--confirmed' : 'pdp-fitment--no-fit');
            window.dispatchEvent(new CustomEvent('fitment:verified', { detail: { fits: fits } }));
          } else if (this.fitCount > 0) {
            fits = this.fitHandles[vehicle.id] === true;
            b.classList.add(fits ? 'pdp-fitment--confirmed' : 'pdp-fitment--no-fit');
            window.dispatchEvent(new CustomEvent('fitment:verified', { detail: { fits: fits } }));
          } else {
            // No fitment data to check against — vehicle is set, allow add to cart.
            window.dispatchEvent(new CustomEvent('fitment:verified', { detail: { fits: true } }));
          }
        }
      }
      if (nameEl) nameEl.textContent = vehicle.display || [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].filter(Boolean).join(' ');
    },

    updateShopBtn: function (vehicle, storefrontUrl, storefrontToken) {
      var shopBtn = document.querySelector('[data-fitment-shop-vehicle]');
      if (!shopBtn) return;
      if (!vehicle || !window.FitmentCollections) {
        shopBtn.style.display = 'none';
        return;
      }
      window.FitmentCollections.forVehicle(vehicle.id, storefrontUrl, storefrontToken)
        .then(function (coll) {
          if (coll) {
            shopBtn.href = '/collections/' + coll;
            shopBtn.classList.remove('pdp-cta-btn--disabled');
            shopBtn.setAttribute('aria-disabled', 'false');
            shopBtn.style.display = '';
          } else {
            shopBtn.style.display = 'none';
          }
        });
    }
  };

  /* ── Add-ons ── */
  /*
   * An add-on button reads "ADD TO CART", exactly like the product's own. When the product cannot
   * be added — no vehicle chosen, or a vehicle the system does not fit — the resolver disables the
   * product ATC, and the add-on button becomes the only enabled "ADD TO CART" on the page. A
   * shopper who clicks it carts a bottle of brake fluid and no brake system.
   *
   * So the add-ons follow the product: available only while the product itself is. Buying an
   * accessory for a part you cannot buy is never the intent, and the accessory has its own product
   * page for the case where it genuinely is.
   *
   * The gate is re-evaluated on every mutation of the product ATC because the resolver flips it
   * asynchronously, long after this runs.
   */
  var FyreAddons = {
    _sync: function () {
      var main = document.querySelector('[data-pdp-atc]');
      // No product ATC on this template — nothing to be confused with, so nothing to gate. Restore
      // rather than return: the click handler disables the button while its request is in flight and
      // relies on this function to put it back, so an early return here left add-ons one-shot per
      // page load on any template where the merchant removed the ATC block.
      var blocked = main ? !!main.disabled : false;
      document.querySelectorAll('[data-addon-variant-id]').forEach(function (b) {
        if (b.dataset.addonBusy === '1') return;   // mid-request; its own handler owns the state
        b.disabled = blocked;
        // Deliberately NOT worded as a fitment message. The product ATC is disabled for stock, a
        // pending add, and a failed fitment verification as well as for a non-fitting vehicle, and
        // naming only one of those reasons is wrong on all the others. The `pdp-atc-btn--needs-fitment`
        // class looks like the discriminator and is not: assets/fyre-pdp.js sets it, and
        // assets/fyre-resolve.js — which owns the ATC on every builder umbrella, the case this whole
        // guard exists for — never does.
        b.title = blocked ? 'Available once this product can be added to the cart' : '';
      });
    },
    init: function () {
      var self = this;
      var main = document.querySelector('[data-pdp-atc]');
      if (main) {
        new MutationObserver(function () { self._sync(); })
          .observe(main, { attributes: true, attributeFilter: ['disabled'] });
      }
      self._sync();

      document.querySelectorAll('[data-addon-variant-id]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          // Fail-closed predicate, not merely a disabled attribute: a stale or restyled button
          // must not be able to cart an accessory the product itself cannot be bought with.
          var m = document.querySelector('[data-pdp-atc]');
          if (m && m.disabled) return;
          var vid = parseInt(btn.dataset.addonVariantId);
          if (!vid) return;
          btn.dataset.addonBusy = '1';
          btn.disabled = true;
          btn.textContent = 'ADDING...';
          // A request that never settles would otherwise leave this button disabled and busy for the
          // rest of the page's life, skipped by every subsequent sync. Same AbortController pattern
          // the resolver's gated fetch already uses.
          var ctl = window.AbortController ? new AbortController() : null;
          var killed = ctl ? setTimeout(function () { ctl.abort(); }, 10000) : null;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
            signal: ctl ? ctl.signal : undefined
          })
            .then(function (res) {
              if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || 'Failed'); });
              return res.json();
            })
            .then(function () {
              if (killed) clearTimeout(killed);
              btn.innerHTML = '<i class="fa-regular fa-check" aria-hidden="true"></i> ADDED';
              // busy is held until the label is restored. Clearing it here would let a sync re-enable
              // a button still reading "ADDED", and a second click would add a duplicate line.
              setTimeout(function () {
                btn.innerHTML = '<i class="fa-regular fa-plus" aria-hidden="true"></i> ADD TO CART';
                delete btn.dataset.addonBusy;
                FyreAddons._sync();
              }, 2000);
              document.dispatchEvent(new CustomEvent('atc:added', { detail: {} }));
            })
            .catch(function () {
              if (killed) clearTimeout(killed);
              btn.textContent = 'ERROR';
              setTimeout(function () {
                btn.innerHTML = '<i class="fa-regular fa-plus" aria-hidden="true"></i> ADD TO CART';
                delete btn.dataset.addonBusy;
                FyreAddons._sync();
              }, 2000);
            });
        });
      });
    }
  };

  /* ── Flyout Info Panel ── */
  var FyreFlyout = {
    el: null, overlay: null, contentEl: null, titleEl: null,

    init: function () {
      var html =
        '<div class="pdp-flyout" data-pdp-flyout>' +
          '<div class="pdp-flyout__overlay" data-flyout-close></div>' +
          '<div class="pdp-flyout__panel">' +
            '<div class="pdp-flyout__header">' +
              '<p class="pdp-flyout__title" data-flyout-title></p>' +
              '<button type="button" class="pdp-flyout__close" data-flyout-close aria-label="Close">' +
                '<i class="fa-regular fa-times" aria-hidden="true"></i>' +
              '</button>' +
            '</div>' +
            '<div class="pdp-flyout__content" data-flyout-content></div>' +
          '</div>' +
        '</div>';
      document.body.insertAdjacentHTML('beforeend', html);
      this.el = document.querySelector('[data-pdp-flyout]');
      this.contentEl = this.el.querySelector('[data-flyout-content]');
      this.titleEl = this.el.querySelector('[data-flyout-title]');

      var self = this;
      this.el.querySelectorAll('[data-flyout-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });

      var tooltipData = null;
      try {
        var tdEl = document.getElementById('pdp-tooltip-data');
        if (tdEl) {
          tooltipData = JSON.parse(tdEl.textContent);
        }
      } catch (e) { /* tooltipData stays null */ }

      function buildTooltipHtml(data) {
        var html = '';
        var introText = data.intro ? data.intro.replace(/<[^>]*>/g, '').trim() : '';
        if (introText) html += '<div class="pdp-tooltip-content__intro">' + data.intro + '</div>';
        if (data.blocks && data.blocks.length) {
          var solo = data.blocks.length === 1 && !introText;
          for (var i = 0; i < data.blocks.length; i++) {
            var b = data.blocks[i];
            html += '<div class="pdp-tooltip-content__block' + (solo ? ' pdp-tooltip-content__block--solo' : '') + '">';
            if (b.image) html += '<img src="' + b.image + '" class="pdp-tooltip-content__img" loading="lazy" width="400">';
            if (b.title) html += '<p class="pdp-tooltip-content__block-title">' + b.title + '</p>';
            if (b.body) html += '<div class="pdp-tooltip-content__block-body">' + b.body + '</div>';
            html += '</div>';
          }
        }
        return html || null;
      }

      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-tooltip-key]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          var label = trigger.closest('.pdp-option__label-row, .pdp-complete__header, .pdp-info__spec-th-inner');
          var title = label ? (label.querySelector('.pdp-option__label-name') || label.querySelector('.pdp-complete__title') || label.querySelector('.pdp-spec-label')) : null;
          var key = trigger.getAttribute('data-tooltip-key');
          var tipData = tooltipData && tooltipData[key];
          self.open(
            title ? title.textContent.replace(/:$/, '').trim() : trigger.getAttribute('aria-label') || 'INFO',
            tipData ? buildTooltipHtml(tipData) : null
          );
        }
      });
    },

    open: function (title, html) {
      if (!html) return;
      this.titleEl.textContent = title;
      this.contentEl.innerHTML = html;
      this.el.classList.add('pdp-flyout--open');
      document.body.style.overflow = 'hidden';
    },

    close: function () {
      this.el.classList.remove('pdp-flyout--open');
      setTimeout(function () { document.body.style.overflow = ''; }, 300);
    }
  };

  /* ── Drawing Lightbox ── */
  var FyreDrawingLightbox = {
    el: null,

    init: function () {
      var html =
        '<div class="pdp-lightbox" data-pdp-lightbox>' +
          '<div class="pdp-lightbox__overlay" data-lightbox-close></div>' +
          '<div class="pdp-lightbox__inner">' +
            '<button type="button" class="pdp-lightbox__close" data-lightbox-close aria-label="Close">' +
              '<i class="fa-regular fa-times" aria-hidden="true"></i>' +
            '</button>' +
            '<img class="pdp-lightbox__img" data-lightbox-img alt="Technical drawing" width="800" height="600">' +
          '</div>' +
        '</div>';
      document.body.insertAdjacentHTML('beforeend', html);
      this.el = document.querySelector('[data-pdp-lightbox]');

      var self = this;
      this.el.querySelectorAll('[data-lightbox-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.el.classList.contains('pdp-lightbox--open')) self.close();
      });
    },

    open: function (src) {
      var img = this.el.querySelector('[data-lightbox-img]');
      if (img) img.src = src;
      this.el.classList.add('pdp-lightbox--open');
      document.body.style.overflow = 'hidden';
    },

    close: function () {
      this.el.classList.remove('pdp-lightbox--open');
      document.body.style.overflow = '';
    }
  };

  /* ── Fitment Note (vehicle × SKU specific note) ──
     Shows a fitment_note ONLY when the active variant SKU is in the note's `skus`
     AND the active fitment vehicle is reached either by the note's own `vehicles`
     references or by one of its `groups` (a fitment_group the product carries).
     Slot stays hidden otherwise.

     The two grains are alternatives, not a fallback chain: notes are authored against
     a fitment_group where one exists, and against fitment_vehicle references directly
     where none does (power-sports SKUs in particular carry vehicles and no group at
     all). Gating on groups alone makes every such note unreachable.

     WHY a group and not the product-wide fit verdict: a group is the only thing that
     ties a note to BOTH a product and a vehicle. The fit verdict is not a substitute —
     a product carrying two groups fits every vehicle in either, so gating a note on
     "this vehicle fits this product" would print group A's guidance on a group B
     vehicle. These notes are safety copy (pad/line/road-use warnings), so a wrong-
     vehicle SHOW is the failure mode this gate exists to prevent. */
  var FyreFitmentNote = {
    records: [], sku: null, slot: null, textEl: null, staticNotes: null,
    // Per-group vehicle membership, keyed by fitment_group handle:
    //   undefined = never asked | 'pending' = in flight | null = fetch failed (fail-closed, no note)
    //   object    = complete handle set for that group
    groupVehicles: {}, productGroups: null, sfUrl: null, sfToken: null,

    // Bump when the record SHAPE changes. The TTL covers the other axis: note DATA can change under a
    // stable shape, and sessionStorage would otherwise pin it for the whole tab session.
    // v2: records carry `groups` (fitment_group handles) instead of the retired `vehicle_handles` text list.
    // v3: records also carry `vehicles` — the note's OWN fitment_vehicle references. A v2 payload has no
    // such key, so serving it would keep every direct-vehicle note unmatchable for the rest of the tab.
    CACHE_KEY: 'fyre_fitment_notes_v3',
    CACHE_TTL_MS: 1800000,      // 30 min
    FETCH_TIMEOUT_MS: 8000,
    // Runaway bound ONLY — termination is already guaranteed by the seen-cursor guard below; this
    // exists solely for a server that emits endlessly NEW cursors. Set far above any plausible
    // corpus (measured: permits 201 pages = 50,250 notes, vs 417 today — 120x headroom) because tripping it empties
    // records and kills EVERY note, so a bound set close to real volume would convert corpus growth
    // into a total outage.
    MAX_PAGES: 200,
    // Reference-truncation drops stop being an authoring observation and become a systemic fault
    // above BOTH of these: a share of the committed corpus, and an absolute floor. The floor is
    // what keeps "a handful" literal — without it, one bad note against two good ones is 33% and
    // would escalate. See the badRefs grade in load().
    TRUNC_DROP_RATIO: 0.1,
    TRUNC_DROP_MIN: 5,

    init: function (sectionId) {
      var notesEl = document.getElementById('pdp-fitment-notes-' + sectionId);
      var section = document.querySelector('[data-pdp-section="' + sectionId + '"]') || document;
      this.slot = section.querySelector('[data-fitment-note-dynamic]');
      this.textEl = section.querySelector('[data-fitment-note-text]');
      // Static notes (theme-setting note + specs.fitment_notes) — hidden while a dynamic note shows (dynamic wins),
      // so the one UI region never stacks two info icons.
      this.staticNotes = section.querySelectorAll('.pdp-fitment__note:not(.pdp-fitment__note--dynamic)');
      if (!this.slot) return;

      // The product's OWN fitment_group handles, emitted server-side as ||a||b|| on
      // #pdp-fitment-data-<sid> (the same `seen_g` the fit-handle capture dedups with). Used as a
      // cheap synchronous pre-filter so a note authored for another product's group never costs a
      // fetch. Deliberately NOT a hard gate: that capture reads VARIANT-level groups only and is
      // subject to Shopify's 50-item metaobject expansion cap, so it is EMPTY on product-level-
      // groups products (auto discs) and short on large ones. Empty ⇒ no pre-filter, and the group→
      // vehicle check below still decides — same "empty/missing = fall back" contract FyreFitment
      // uses for ownFitHandles. A false HIDE here would silently drop safety copy.
      var groupsEl = document.getElementById('pdp-fitment-data-' + sectionId);
      if (groupsEl) {
        this.sfUrl = groupsEl.dataset.storefrontUrl || null;
        this.sfToken = groupsEl.dataset.storefrontToken || null;
        var raw = groupsEl.dataset.fitmentGroups || '';
        var pg = {}, any = false;
        raw.split('||').forEach(function (h) { h = h.trim(); if (h) { pg[h] = true; any = true; } });
        this.productGroups = any ? pg : null;
      }

      var self = this;
      // Listeners register BEFORE the fetch resolves. A vehicle change or a resolver event landing
      // mid-flight must not be dropped, and evaluate() is safe against an empty record set.
      window.addEventListener('fitment:changed', function () { self.evaluate(); });
      // Builder umbrellas: the resolver (fyre-resolve.js) signals the resolved backing SKU here. FyreProductForm only
      // ever sees the null umbrella SKU, so without this the note can never match on a builder kit.
      window.addEventListener('fyre:resolved', function (e) { self.setSku(e.detail && e.detail.sku); });
      // Cache-boot race: on a warm cache the resolver resolves in a microtask that can flush BEFORE this listener
      // registers (init runs on DOMContentLoaded), so the initial fyre:resolved is missed and the note never shows
      // until the user changes an option. Seed the already-resolved SKU from the root dataset.
      var fr = document.querySelector('[data-fyre-resolve]');
      if (fr && fr.dataset.resolvedSku) this.sku = fr.dataset.resolvedSku;

      this.evaluate();                                    // hide the slot until records arrive
      this.load(notesEl).then(function () { self.evaluate(); });

      // Back/forward cache: a restored page never re-fires DOMContentLoaded, so a vehicle changed
      // on another page would leave this PDP showing the PREVIOUS vehicle's guidance — silently, and
      // this is safety copy. Re-running load() also re-checks the cache TTL, so a page parked in
      // bfcache past the TTL refetches instead of serving expired records.
      window.addEventListener('pageshow', function (e) {
        if (!e || !e.persisted) return;
        self.load(notesEl).then(function () { self.evaluate(); });
      });
    },

    // Fetches every fitment_note from the Storefront API, following the cursor to exhaustion.
    // WHY a fetch: an inline `{% paginate ... by 250 %}` dump renders ONE page per HTTP request,
    // so only the first 250 of 417 notes ever reached the browser — products showed a PARTIAL set with
    // no signal. JS pagination has no such ceiling.
    //
    // ALL-OR-NOTHING, deliberately. A partial traversal is NEVER assigned or cached: committing half a
    // page set would recreate the silent-partial-notes failure, except cached for the whole session. Any failure — HTTP, GraphQL `errors` on a 200, malformed pageInfo, a missing or
    // non-advancing cursor, a timeout, or exceeding the page bound — throws and lands in the catch,
    // which empties records so the STATIC notes stay visible instead.
    load: function (cfg) {
      var self = this;
      if (!cfg || !cfg.dataset.storefrontUrl || !cfg.dataset.storefrontToken) return Promise.resolve();

      // notesError describes THIS load. Left standing from a previous attempt it would report a
      // succeeding PDP as broken, and the flag is only useful if it can go back down.
      try { if (this.slot) delete this.slot.dataset.notesError; } catch (e) { /* diagnostics never break the PDP */ }

      // Theme editor: a merchant edits a note and reloads the preview to check it. Serving a cached
      // payload there shows the OLD text and reads as "my edit didn't save". The editor is exactly
      // where freshness matters more than a round trip. Same bypass idiom as fyre-global.js.
      var designMode = !!(window.Shopify && window.Shopify.designMode);

      // TTL'd envelope: sessionStorage survives navigation and reloads for the whole tab, so a bare
      // cache would pin removed or newly-added guidance for the entire session.
      if (!designMode) {
        try {
          var raw = sessionStorage.getItem(this.CACHE_KEY);
          if (raw) {
            var env = JSON.parse(raw);
            if (env && Array.isArray(env.records) && (Date.now() - env.ts) < this.CACHE_TTL_MS) {
              this.records = env.records;
              return Promise.resolve();
            }
          }
        } catch (e) { /* private mode, quota, or corrupt entry — fall through to the network */ }
      }

      // TRAPS on this query, both confirmed against the live schema:
      //  1. The Storefront API returns ONLY metaobjects whose publishable status is ACTIVE, and
      //     `publishable` IS enabled on fitment_note — so a note authored as DRAFT vanishes from the
      //     PDP with no error. That is a documented platform rule, not a bug, but it means "note is
      //     missing" can mean "note is DRAFT". Check status before debugging this code.
      //  2. `metaobjectDefinitionByType.metaobjectsCount` is STALE on this store — it reports 312
      //     against 417 real records (verified: 417 unique ids from Admin AND Storefront, 0 dupes).
      //     Never use that counter to sanity-check this fetch; paginate and count ids.
      var url = cfg.dataset.storefrontUrl, token = cfg.dataset.storefrontToken, out = [];
      var dropped = 0, badRefs = 0, badSkus = 0;
      // `skus` is a list field, so the parsed value MUST be an array. A scalar string parses
      // cleanly and then makes `r.skus.indexOf(sku)` a SUBSTRING match — a note authored for
      // SKU "105998709" would render on variant SKU "1059". That is the one fail-OPEN path in a
      // module whose whole job is to fail closed, so a non-array becomes [] and the note is
      // dropped and counted rather than committed against an unverifiable SKU scope.
      var field = function (f) {
        try {
          var v = f && f.value ? JSON.parse(f.value) : [];
          return Array.isArray(v) ? v : [];
        } catch (e) { return []; }
      };
      // A reference connection that is TRUNCATED silently narrows ONE note's vehicle scope — that
      // note then hides on the vehicles that fell off the page, with no error anywhere.
      //
      // The blast radius is deliberately ONE NOTE, not the corpus. ensureGroup() is all-or-nothing
      // because its whole query IS one group, so failing it costs exactly that group; throwing from
      // here would instead discard all ~413 notes because a single one outgrew 250 references. Return
      // null, drop that note, count it, and commit the rest.
      var refNodes = function (f) {
        var refs = f && f.references;
        if (!refs) return [];
        if (!Array.isArray(refs.nodes) || !refs.pageInfo ||
            typeof refs.pageInfo.hasNextPage !== 'boolean' || refs.pageInfo.hasNextPage) {
          return null;   // truncated or malformed — caller drops this note only
        }
        return refs.nodes;
      };

      function page(cursor, depth, seen) {
        if (depth > self.MAX_PAGES) throw new Error('fitment-notes: page bound exceeded');
        var after = cursor ? ', after:"' + cursor + '"' : '';
        var q = '{ metaobjects(type:"fitment_note", first:250' + after + ') { edges { node {' +
                ' note: field(key:"note"){value} skus: field(key:"skus"){value}' +
                ' groups: field(key:"groups"){ references(first:250){ pageInfo { hasNextPage } nodes { ... on Metaobject { handle } } } }' +
                ' vehicles: field(key:"vehicles"){ references(first:250){ pageInfo { hasNextPage } nodes { ... on Metaobject { handle } } } }' +
                ' } } pageInfo { hasNextPage endCursor } } }';

        // A stalled request must reach the static-note fallback rather than hang the notes forever.
        // The timer stays ARMED until the body has parsed AND validated — clearing it when headers
        // arrive leaves a response that stalls mid-body pending indefinitely, never aborting.
        var ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var timer = null;
        var disarm = function () { if (timer) { clearTimeout(timer); timer = null; } };
        if (ctl) timer = setTimeout(function () { ctl.abort(); }, self.FETCH_TIMEOUT_MS);

        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({ query: q }),
          signal: ctl ? ctl.signal : undefined
        }).then(function (r) {
          if (!r.ok) throw new Error('fitment-notes: HTTP ' + r.status);
          return r.json();          // timer deliberately still armed — a stalled body must abort
        }).then(function (j) {
          // A GraphQL error arrives with HTTP 200. Returning quietly here is what would let a partial
          // set be cached as authoritative.
          if (!j || j.errors) throw new Error('fitment-notes: GraphQL error');
          var mo = j.data && j.data.metaobjects;
          // Validate the SHAPE, not merely presence. A later page carrying `pageInfo: {}` leaves
          // hasNextPage undefined, which reads as falsy — i.e. "traversal complete" — and would commit
          // and cache a partial corpus. hasNextPage must be an actual boolean, and when true the
          // cursor must be a non-empty string.
          if (!mo || !Array.isArray(mo.edges) || !mo.pageInfo ||
              typeof mo.pageInfo.hasNextPage !== 'boolean') {
            throw new Error('fitment-notes: malformed response');
          }
          disarm();                 // body parsed and shape-validated — safe to stand down

          for (var i = 0; i < mo.edges.length; i++) {
            var n = mo.edges[i] && mo.edges[i].node;
            if (!n) continue;
            // `groups` and `vehicles` are both list.metaobject_reference, so their handles come off
            // `references.nodes` rather than a JSON-encoded text value. The two are alternative grains:
            // most notes are authored against fitment_groups, but a large share carry fitment_vehicle
            // references DIRECTLY and no group at all (power-sports SKUs in particular). A note with
            // NEITHER can never satisfy the vehicle half of the gate, so only that case is dropped.
            var groups = [], vehicles = [], skus = field(n.skus);
            var gnodes = refNodes(n.groups), vnodes = refNodes(n.vehicles);
            // A partial reference list is indistinguishable from a note that genuinely does not name
            // the vehicle, so this note is dropped rather than rendered against an unverified scope.
            if (gnodes === null || vnodes === null) { badRefs++; continue; }
            for (var g = 0; g < gnodes.length; g++) {
              if (gnodes[g] && gnodes[g].handle) groups.push(gnodes[g].handle);
            }
            for (var v = 0; v < vnodes.length; v++) {
              if (vnodes[v] && vnodes[v].handle) vehicles.push(vnodes[v].handle);
            }
            if (n.note && n.note.value && skus.length && (groups.length || vehicles.length)) {
              out.push({ note: n.note.value, skus: skus, groups: groups, vehicles: vehicles });
            } else if (n.note && n.note.value && skus.length) {
              // Neither grain: unmatchable by construction. Dropping it silently is what made the
              // direct-vehicle defect invisible, so the count is surfaced once per traversal.
              dropped++;
            } else if (n.note && n.note.value) {
              // A note with copy but no usable SKU list — missing, empty, unparseable, or the
              // non-array shape guarded above. It was previously dropped by NO counter at all,
              // so a field-type change on the metaobject definition would empty the corpus in
              // total silence. Every drop class REACHABLE WITH NOTE COPY is now counted — a node
              // whose `note` field is absent or empty falls through all three branches
              // uncounted, because it carries nothing to render and nothing to diagnose.
              badSkus++;
            }
          }

          if (!mo.pageInfo.hasNextPage) return;
          var next = mo.pageInfo.endCursor;
          // A missing, non-string, or repeated cursor would re-request the same page forever.
          if (typeof next !== 'string' || !next || seen[next]) {
            throw new Error('fitment-notes: cursor did not advance');
          }
          seen[next] = true;
          return page(next, depth + 1, seen);
        }).catch(function (err) { disarm(); throw err; });
      }

      return new Promise(function (resolve) { resolve(page(null, 0, {})); })
        .then(function () {
          self.records = out;   // reached only on a COMPLETE traversal
          // A well-formed EMPTY response is not a legitimate steady state for this shop — there are
          // hundreds of live notes, so zero means the metaobject definition lost Storefront access,
          // the token lost `unauthenticated_read_metaobjects`, or the type was renamed. Caching that
          // pins "no guidance" for the whole TTL, which is the silent-zero bug in new clothes.
          // Treat it as UNPROVEN: use it for this render, never persist it.
          if (dropped) self.note(dropped + ' fitment notes dropped: no groups and no vehicles');
          if (badSkus) self.note(badSkus + ' fitment notes dropped: skus missing, empty, or not a list');
          // Grade the truncation drop on RATIO, not presence. A handful of oversized notes is an
          // authoring observation; losing a tenth of the corpus to truncation is a systemic
          // regression that would otherwise commit, cache for 30 minutes, and read as healthy.
          // Both grades stay CONSOLE-ONLY: the records that did commit still render, so raising
          // notesError — the attribute that means "this load failed" — would be a lie.
          var systemic = badRefs >= self.TRUNC_DROP_MIN && badRefs > out.length * self.TRUNC_DROP_RATIO;
          if (badRefs) {
            self.note(badRefs + ' fitment notes dropped: reference list truncated/malformed' +
              (systemic
                ? ' — SYSTEMIC: over ' + Math.round(self.TRUNC_DROP_RATIO * 100) +
                  '% of the committed corpus, check the reference page size'
                : ''));
          }
          if (!out.length) { self.warn('empty note set — not caching (check metaobject Storefront access)'); return; }
          // Same doctrine as the empty set, for the same reason: a systemically truncated corpus
          // is UNPROVEN, so it renders for THIS page view and is never pinned for the 30-minute
          // TTL. Persisting it is what let "9 of 10 notes lost" commit one record and read as a
          // healthy load on every subsequent page for half an hour.
          if (systemic) { self.note('systemic truncation — not caching this corpus'); return; }
          // Evicted BEFORE the write and outside its try: the v2 corpus predates `vehicles` and is
          // never served again, so a v3 write that fails on quota must not leave the dead copy
          // occupying the budget that write needed.
          try { sessionStorage.removeItem('fyre_fitment_notes_v2'); } catch (e) { /* private mode */ }
          try {
            sessionStorage.setItem(self.CACHE_KEY, JSON.stringify({ ts: Date.now(), records: out }));
          } catch (e) {
            // A cache miss costs one refetch next page load; the notes on THIS page are unaffected.
            // Routing it through warn() would report a working PDP as a failed load.
            self.note('cache write failed: ' + (e && e.message ? e.message : 'quota or private mode'));
          }
        })
        .catch(function (err) {
          self.records = [];    // partial data is never committed
          // Never swallow. A 429, a rotated token, or API drift otherwise produces zero notes exactly
          // as silently as the bug this branch exists to remove, and is undiagnosable from a browser.
          // Matches the convention fyre-pdp-vehicle-compat.js already documents for this same surface.
          self.warn(err && err.message ? err.message : 'fetch failed');
        });
    },

    // Single failure surface: console for a human, a data attribute for QA and monitoring to assert on.
    // Reserved for a load that FAILED — see note() for the observation that is not a failure.
    warn: function (msg) {
      this.note(msg);
      try {
        if (this.slot) this.slot.dataset.notesError = '1';
      } catch (e) { /* never let diagnostics break the PDP */ }
    },

    // Console only, no notesError. Authoring observations are not load failures: dozens of live
    // notes legitimately carry neither grain, so raising the QA attribute on every successful load
    // would make the one signal that means "the notes are broken" mean nothing at all.
    note: function (msg) {
      try {
        if (window.console && console.warn) console.warn('[fitment-notes] ' + msg);
      } catch (e) { /* never let diagnostics break the PDP */ }
    },

    setSku: function (sku) { this.sku = sku || null; this.evaluate(); },

    currentVehicleId: function () {
      try {
        var v = (window.FitmentStorage ? window.FitmentStorage.get() : null)
                || JSON.parse(localStorage.getItem('fitment_vehicle') || 'null');
        return v ? v.id : null;
      } catch (e) { return null; }
    },

    // Cheap synchronous pre-filter — see the productGroups note in init(). Unknown (empty capture)
    // means "cannot pre-filter", never "not carried".
    carriesGroup: function (handle) {
      return !this.productGroups || this.productGroups[handle] === true;
    },

    // Resolve ONE fitment_group's vehicle handles, once, and cache by group handle.
    //
    // WHY this is not read off an existing membership set: fyre-pdp.js's ownFitHandles/fitHandles
    // and fyre-pdp-vehicle-compat.js's collectGroupVehicles both UNION every group's vehicles into
    // a single flat set and discard the group key, which answers "does this vehicle fit this
    // product" — not "which group does it fit through". Only the second question can gate a
    // per-group note. Scoped by group handle and reached only after a SKU match, so a PDP resolves
    // one or two small groups, not the product's whole vehicle universe.
    //
    // Same safety contract as load(): all-or-nothing (a partial page set is never committed),
    // armed timeout, seen-cursor guard, errors surfaced not swallowed. A failure caches null =
    // fail-closed: the note stays hidden rather than showing against an unverified vehicle.
    ensureGroup: function (handle) {
      var self = this;
      if (this.groupVehicles[handle] !== undefined) return;   // resolved, failed, or in flight
      if (!this.sfUrl || !this.sfToken) { this.groupVehicles[handle] = null; return; }
      this.groupVehicles[handle] = 'pending';

      var acc = {};
      function page(cursor, depth, seen) {
        if (depth > self.MAX_PAGES) throw new Error('fitment-notes: group page bound exceeded');
        var after = cursor ? ', after:' + JSON.stringify(cursor) : '';
        var q = '{ metaobject(handle:{type:"fitment_group", handle:' + JSON.stringify(handle) + '}) {' +
                ' vehicles: field(key:"vehicles"){ references(first:250' + after + ')' +
                ' { pageInfo { hasNextPage endCursor } nodes { ... on Metaobject { handle } } } } } }';

        var ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var timer = null;
        var disarm = function () { if (timer) { clearTimeout(timer); timer = null; } };
        if (ctl) timer = setTimeout(function () { ctl.abort(); }, self.FETCH_TIMEOUT_MS);

        return fetch(self.sfUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': self.sfToken },
          body: JSON.stringify({ query: q }),
          signal: ctl ? ctl.signal : undefined
        }).then(function (r) {
          if (!r.ok) throw new Error('fitment-notes: group HTTP ' + r.status);
          return r.json();          // timer deliberately still armed — a stalled body must abort
        }).then(function (j) {
          // A cost throttle arrives as HTTP 200 + errors[] + data:null. Treating that as "no
          // vehicles" would cache a false empty and hide the note for the rest of the session.
          if (!j || j.errors) throw new Error('fitment-notes: group GraphQL error');
          var mo = j.data && j.data.metaobject;
          var refs = mo && mo.vehicles && mo.vehicles.references;
          if (!refs || !Array.isArray(refs.nodes) || !refs.pageInfo ||
              typeof refs.pageInfo.hasNextPage !== 'boolean') {
            throw new Error('fitment-notes: malformed group response');
          }
          disarm();

          for (var i = 0; i < refs.nodes.length; i++) {
            var v = refs.nodes[i];
            if (v && v.handle) acc[v.handle] = true;
          }

          if (!refs.pageInfo.hasNextPage) return;
          var next = refs.pageInfo.endCursor;
          if (typeof next !== 'string' || !next || seen[next]) {
            throw new Error('fitment-notes: group cursor did not advance');
          }
          seen[next] = true;
          return page(next, depth + 1, seen);
        }).catch(function (err) { disarm(); throw err; });
      }

      new Promise(function (resolve) { resolve(page(null, 0, {})); })
        .then(function () {
          self.groupVehicles[handle] = acc;   // reached only on a COMPLETE traversal
          self.evaluate();
        })
        .catch(function (err) {
          self.groupVehicles[handle] = null;  // fail-closed; never retried this page view
          self.warn('group ' + handle + ': ' + (err && err.message ? err.message : 'fetch failed'));
          self.evaluate();
        });
    },

    evaluate: function () {
      if (!this.slot) return;
      var vid = this.currentVehicleId();
      var sku = this.sku;
      // A vehicle x SKU can carry SEVERAL notes: the client's source packs them into one
      // semicolon-joined cell and the importer splits each into its own metaobject, so the
      // shared sentences ("May require new brake lines") stay one record across many vehicles.
      // filter, not find — find() would show the first and silently drop the rest.
      var self = this;
      // SKU first — it is synchronous and cuts the corpus to a handful, so the group→vehicle
      // resolution below only ever runs for notes that could actually apply to this variant.
      var skuMatches = (vid && sku)
        // Array.isArray, not truthiness: a cached record from a foreign or older envelope can
        // carry a STRING here, and `'105998709'.indexOf('1059')` is a substring hit — the note
        // would show against a vehicle nobody authored it for. The loader guards the network
        // path; this guards the cache path, which never passes through field().
        ? this.records.filter(function (r) { return Array.isArray(r.skus) && r.skus.indexOf(sku) !== -1; })
        : [];

      // A note that names the active vehicle DIRECTLY is already decided — synchronously, and with
      // no group indirection. Settle those first so they neither wait on nor trigger a group fetch.
      // Array.isArray for the same reason as `skus` above, and against the same envelope: a
      // STRING here makes indexOf a substring test, so `vehicles: 'veh-A-competition'` shows the
      // note on vid `veh-A` — a wrong-vehicle SHOW on safety copy. Null-safety alone is not enough.
      var direct = function (r) { return Array.isArray(r.vehicles) && r.vehicles.indexOf(vid) !== -1; };

      // Ask for the membership of every group still in play. Unresolved groups kick off one fetch
      // each and re-enter evaluate() on completion; until then they count as NOT containing the
      // vehicle, so the note stays hidden rather than flashing on and off.
      skuMatches.forEach(function (r) {
        if (direct(r)) return;
        // Array.isArray, not `|| []`, because a record can arrive from a foreign or corrupt
        // sessionStorage envelope (the cache read validates the ARRAY, not each record's shape).
        // A STRING groups value is truthy and has no forEach, so `|| []` still throws. evaluate()
        // runs synchronously inside init(), so a TypeError here aborts the rest of init —
        // including the pageshow listener, which would leave bfcache showing the previous
        // vehicle's guidance.
        (Array.isArray(r.groups) ? r.groups : []).forEach(function (h) { if (self.carriesGroup(h)) self.ensureGroup(h); });
      });

      var matches = skuMatches.filter(function (r) {
        if (direct(r)) return true;
        return (Array.isArray(r.groups) ? r.groups : []).some(function (h) {
          var set = self.groupVehicles[h];
          return !!set && set !== 'pending' && set[vid] === true;
        });
      });
      if (matches.length) {
        this.renderNotes(matches);
        this.slot.hidden = false;
        this.toggleStatic(true);  // dynamic wins: hide the static notes
      } else {
        this.slot.hidden = true;
        this.toggleStatic(false); // restore static notes
      }
    },

    // Renders one line per note. Splits on ';' as well so a single record still renders as
    // separate lines if a joined cell was ever imported unsplit; dedups so an overlapping
    // vehicle set can't print the same sentence twice.
    renderNotes: function (matches) {
      if (!this.textEl) return;
      var seen = {}, lines = [], i, j, parts;
      for (i = 0; i < matches.length; i++) {
        parts = String(matches[i].note || '').split(';');
        for (j = 0; j < parts.length; j++) {
          var line = parts[j].trim();
          if (!line || seen[line]) continue;
          seen[line] = true;
          lines.push(line);
        }
      }
      this.textEl.textContent = '';
      for (i = 0; i < lines.length; i++) {
        // div, not span: block is the UA default, so the lines stay separated even if the
        // stylesheet fails to load. Losing CSS must not run two safety notes into one string.
        var el = document.createElement('div');
        el.className = 'pdp-fitment__note-line';
        el.textContent = lines[i];
        this.textEl.appendChild(el);
      }
    },

    toggleStatic: function (hide) {
      if (!this.staticNotes) return;
      for (var i = 0; i < this.staticNotes.length; i++) this.staticNotes[i].hidden = hide;
    }
  };

  /* ── Affirm ── */
  // Affirm's SDK replaces .affirm-as-low-as with its own <a class="affirm-modal-trigger"> containing the
  // "Starting at $X/mo or 0% APR with affirm" copy. Append our CTA as a child span INSIDE that same <a> — since
  // Affirm's click handler lives on the <a> itself, clicking the appended text bubbles up and opens the modal for
  // free, no separate link/handler needed. Re-runs on every mutation because fyre-resolve.js calls
  // affirm.ui.refresh() on variant change, which re-renders the trigger and wipes any child we appended before.
  var FyreAffirm = {
    init: function () {
      var widget = document.querySelector('.affirm-as-low-as');
      if (!widget) return;
      var ctaText = widget.getAttribute('data-affirm-cta');
      if (!ctaText) return;
      function appendCta() {
        var trigger = widget.querySelector('a.affirm-modal-trigger');
        if (!trigger || trigger.querySelector('[data-affirm-cta-el]')) return;
        var span = document.createElement('span');
        span.setAttribute('data-affirm-cta-el', '');
        span.className = 'pdp-affirm__cta';
        span.textContent = ctaText;
        trigger.appendChild(span);
      }
      new MutationObserver(appendCta).observe(widget, { childList: true, subtree: true });
      appendCta();
    }
  };

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', function () {
    var sectionEl = document.querySelector('[data-pdp-section]');
    if (!sectionEl) return;
    var sectionId = sectionEl.dataset.pdpSection;

    FyreGallery.init();
    FyrePrice.init(sectionId);
    FyreFitmentNote.init(sectionId);
    FyreProductForm.init(sectionId);
    FyreQty.init();
    FyreWarranty.init();
    FyrePadPicker.init();
    FyreATC.init(sectionId);
    FyrePairWith.init();
    FyreToggles.init();
    FyreFpsTabs.init();
    FyreDescToggle.init();
    FyreFitment.init();
    FyreAddons.init();
    FyreFlyout.init();
    // FyreDrawingLightbox stays uninitialized: no trigger is wired to it, and its hidden img trips Lighthouse.
    FyreColorGallery.init();
    FyreAffirm.init();
  });
})();

var FyreColorGallery = {
  init: function () {
    var modal = document.getElementById('color-gallery-modal');
    if (!modal) return;

    var track = modal.querySelector('.color-gallery-modal__track');
    var dots  = modal.querySelectorAll('.color-gallery-modal__dot');
    var slides = modal.querySelectorAll('.color-gallery-modal__slide');
    var current = 0;

    function goTo(idx) {
      current = (idx + slides.length) % slides.length;
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === current); });
    }

    modal.querySelector('.color-gallery-modal__prev') && modal.querySelector('.color-gallery-modal__prev').addEventListener('click', function () { goTo(current - 1); });
    modal.querySelector('.color-gallery-modal__next') && modal.querySelector('.color-gallery-modal__next').addEventListener('click', function () { goTo(current + 1); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); }); });

    var dragStartX = 0, dragging = false;
    track.addEventListener('pointerdown', function (e) {
      dragStartX = e.clientX;
      dragging = true;
      track.setPointerCapture(e.pointerId);
      track.classList.add('is-dragging');
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var offset = e.clientX - dragStartX;
      track.style.transform = 'translateX(calc(-' + (current * 100) + '% + ' + offset + 'px))';
    });
    track.addEventListener('pointerup', function (e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      var diff = e.clientX - dragStartX;
      if (Math.abs(diff) > 50) {
        goTo(diff < 0 ? current + 1 : current - 1);
      } else {
        track.style.transform = 'translateX(-' + (current * 100) + '%)';
      }
    });
    track.addEventListener('pointercancel', function () {
      dragging = false;
      track.classList.remove('is-dragging');
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
    });

    function open() {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      void modal.offsetWidth;
      modal.classList.add('in');
      modal.setAttribute('aria-hidden', 'false');
      goTo(0);
    }

    function close() {
      modal.classList.remove('in');
      modal.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      }, 300);
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-color-gallery-open]')) { e.preventDefault(); open(); }
    });
    modal.querySelector('.color-gallery-modal__close').addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('in')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });
  }
};
